import { useState, useEffect, useRef, useMemo } from 'react'
import { Modal, Button, Form, FloatingLabel, Row, Col, Container, Alert, Tabs, Tab, OverlayTrigger, Tooltip, InputGroup, FormControl, FormSelect, Spinner } from 'react-bootstrap'
import { useNavigate, useLocation } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import Notification from '../../components/Notification'
import OrderItemControls from '../../components/OrderItemControls'
import OrderFieldClearButton from '../../components/OrderFieldClearButton'
import { forceLogout, shouldForceLogout } from '../../services/auth/session'

const API_BASE = process.env.REACT_APP_API_URL || ""
// jednotné volání pro všechny requesty 
async function apiFetch(path, options = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`
  const isJsonBody = typeof options.body === "string"
  const headers = {
    ...(isJsonBody ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
  }
  const res = await fetch(url, {
    ...options,
    credentials: "include",
    headers,
    signal: options.signal,
  })
  if (shouldForceLogout(res.status, path)) {
    forceLogout()
  }
  if (!res.ok) {
    try {
      const data = await res.json()
      throw new Error(data?.message || data?.detail || "Request failed")
    } catch (err) {
      if (err instanceof SyntaxError) {
        throw new Error(await res.text())
      }
      throw err
    }
  }
  const ct = res.headers.get("content-type") || ""
  if (ct.includes("application/json")) return res.json()
  return res.text()
}

// generování id pro položky objednávky, důležité pro dragdrop context
const generateId = () => {
  return `${Date.now()}-${Math.floor(Math.random() * 1000000)}`
}

// helper pro převod formátů
const toNum = (v) => {
  if (v === '' || v === null || v === undefined) return 0
  const n = Number(String(v).replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}
// helper pro zaokrouhlení
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100
  const recalcVybaveni = (item) => {
    const pocet = toNum(item.pocet)
    const uzvybmj = toNum(item.uzvybmj)
    const vybavitNum = Math.max(round2(pocet - uzvybmj), 0)
    let vybavene = '0'
    if (vybavitNum === 0) {
      vybavene = '0'
    } else if (vybavitNum > 0 && vybavitNum < pocet) {
      vybavene = '1'
    } else if (vybavitNum >= pocet) {
      vybavene = '2'
    }
    return {
      vybavit: vybavitNum.toFixed(2).replace('.', ','),
      vybavene
    }
  }
  // přepočty na řádku při automatické změně vložení/odstranění bonus karty
  const recalcRow = (item) => {
    const pocet = toNum(item.pocet)
    const cena = toNum(item.cena)
    const sleva = toNum(item.sleva)
    const dph = toNum(item.dph)
    const vybaveni = recalcVybaveni(item)
    const slevamj = round2(cena * (sleva / 100))
    const celkem = round2(pocet * cena * (1 - sleva / 100))
    const celkemsdph = round2(celkem * (1 + dph / 100))
    const cenasdph = round2(cena * (1 + dph / 100))
    return {
      ...item,
      ...vybaveni,
      slevamj: slevamj.toFixed(2),
      cenasdph: cenasdph.toFixed(2),
      celkem: celkem.toFixed(2),
      celkemsdph: celkemsdph.toFixed(2),
  }
}
// přepočet celkových částek
const prepocitejCelkoveCastky = (items) => {
  let zaklad1 = 0  // 21 %
  let zaklad2 = 0  // 12 %
  let zaklad0 = 0  // 0 %
  let slevasum = 0
  let dph1 = 0
  let dph2 = 0
  // výpočet základů a DPH podle sazby
  items.forEach(item => {
    const pocet = toNum(item.pocet)
    const cena = toNum(item.cena)
    const slevaPct = toNum(item.sleva)         // -100..100 (záporná = přirážka)
    const dphSazba = toNum(item.dph)           // 0/12/21
    const slevaKoef = 1 - (slevaPct / 100)
    const zaklad = pocet * cena * slevaKoef
    const dphCastka = zaklad * (dphSazba / 100)
    const celkembs = pocet * cena * (1 + dphSazba/100)
    const celkemss = pocet * cena * slevaKoef * (1 + dphSazba/100) 
    if (dphSazba === 21) {
      zaklad1 += zaklad        
      dph1 += dphCastka
    } else if (dphSazba === 12) {
      zaklad2 += zaklad
      dph2 += dphCastka
    } else {
      zaklad0 += zaklad
    }
    slevasum += celkembs - celkemss
  })
  // celková částka před zaokrouhlením
  const celkem = zaklad1 + zaklad2 + zaklad0 + dph1 + dph2
  const celkemZaokrouhleno = Math.round(celkem)
  const rozdil = celkem - celkemZaokrouhleno
  // podíly částek s DPH k celkovému součtu
  const podil1 = (zaklad1 + dph1) / celkem
  const podil2 = (zaklad2 + dph2) / celkem
  const podil0 = zaklad0 / celkem
  // rozdělení rozdílu podle podílů
  const celkem1 = zaklad1 + dph1 - rozdil * podil1
  const celkem2 = zaklad2 + dph2 - rozdil * podil2
  const celkem0 = zaklad0 - rozdil * podil0
  // úprava základů a DPH
  const zaklad1upr = celkem1 / 1.21
  const zaklad2upr = celkem2 / 1.12
  const zaklad0upr = celkem0
  const dph1upr = celkem1 - zaklad1upr
  const dph2upr = celkem2 - zaklad2upr
  // finální součty
  const zaklad = zaklad1upr + zaklad2upr + zaklad0upr
  const dph = dph1upr + dph2upr
  const celkemUpr = zaklad + dph
  return {
    zaklad1: zaklad1upr.toFixed(2),
    dph1: dph1upr.toFixed(2),
    zaklad2: zaklad2upr.toFixed(2),
    dph2: dph2upr.toFixed(2),
    zaklad0: zaklad0upr.toFixed(2),
    zaklad: zaklad.toFixed(2),
    dph: dph.toFixed(2),
    celkem: celkemUpr.toFixed(2),
    celkemsleva: slevasum.toFixed(2)
  }
}
  
// převedení datumu do inputu
const formatDateForInput = (value) => {
  if (!value) return ''
  const raw = String(value).trim()
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (m) return `${m[1]}-${m[2]}-${m[3]}`
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// mapování DB struktury objednávky na FE header/items
const mapOrderToForm = (payload) => {
  const headerRow = payload?.header || {}
  const itemsRows = payload?.items || []
  const toStr = (value) => String(value ?? '')
  const toTrim = (value) => toStr(value).trim()
  const blobToText = (value) => {
    if (value === null || value === undefined) return ''
    if (typeof value === 'string') return value.replace(/\r\n/g, '\n')
    if (typeof value === 'object') {
      if (typeof value.value === 'string') return value.value.replace(/\r\n/g, '\n')
      if (typeof value.text === 'string') return value.text.replace(/\r\n/g, '\n')
      if (Array.isArray(value.data)) {
        try {
          return new TextDecoder('utf-8').decode(new Uint8Array(value.data)).replace(/\r\n/g, '\n')
        } catch (_) {
          return ''
        }
      }
    }
    return toStr(value).replace(/\r\n/g, '\n')
  }
  const toEmptyIfZero = (value) => (toNum(value) === 0 ? '' : toTrim(value))
  const toZakazkaString = (value) => {
    const raw = toTrim(value)
    if (!raw) return ''
    const normalized = raw.replace(',', '.')
    const isPureNumeric = /^[-+]?\d+(\.\d+)?$/.test(normalized)
    if (isPureNumeric && Number(normalized) === 0) return ''
    return raw
  }
  const toWholeNumberString = (value) => {
    const raw = toTrim(value)
    if (!raw) return ''
    const n = Number(raw.replace(',', '.'))
    if (Number.isFinite(n)) return String(Math.trunc(n))
    return raw.replace(/[.,].*$/, '').replace(/\D/g, '')
  }
  const mapUhrada = (value) => {
    const v = toTrim(value).toLowerCase()
    if (!v) return 'hotovost'
    if (v.includes('faktur')) return 'faktura'
    if (v.includes('hotov')) return 'hotovost'
    if (v === 'faktura') return 'faktura'
    if (v === 'hotovost') return 'hotovost'
    return 'hotovost'
  }
  const toFixed2 = (value) => toNum(value).toFixed(2)
  const splitText = (value) => {
    const text = toStr(value).trim()
    if (!text) return { first: '', rest: '' }
    const [first, ...rest] = text.split(/\s+/)
    return { first: first || '', rest: rest.join(' ') }
  }

  const cislo = toTrim(headerRow.CISLO)

  let bonus = ''
  let idrbonus = ''
  let bonusjmeno = ''
  let bonustelefon = ''
  let bonusemail = ''
  let bonusadresa = ''

  const items = []

  for (const row of itemsRows) {
    const text = toStr(row.TEXT).trim()
    const typPol = toStr(row.TYP_POL).trim().toUpperCase()
    const typRadku = toTrim(row.TYP_RADKU) || '1'
    
    const isBonus = typPol === 'BK' || text.toLowerCase().includes('bonus program')
    if (isBonus) {
      const bonusText = splitText(text)
      bonus = toWholeNumberString(bonusText.first)
      idrbonus = toStr(row.IDRKAR)
      bonusjmeno = toStr(row.KOD1)
      bonustelefon = toStr(row.KOD2)
      bonusemail = toStr(row.KOD3)
      bonusadresa = toStr(row.POZNAMKA)
      continue
    }

    if (typRadku === '2') {
      items.push({
        id: generateId(),
        idrkar: '',
        karta: '',
        nazev: text,
        mj: '',
        typpol: '',
        typradku: '2',
        hmotnost: '',
        pocet: '',
        cena: '',
        dph: '',
        sleva: '',
        slevamj: '',
        cenasdph: '',
        celkem: '',
        celkemsdph: '',
        vybavene: toStr(row.VYBAVENE),
        vybavit: toStr(row.VYBAVIT),
        uzvybmj: toStr(row.UZVYBMJ)
      })
      continue
    }

    const parsed = splitText(text)
    const mappedRow = recalcRow({
      id: generateId(),
      idrkar: toStr(row.IDRKAR),
      karta: parsed.first,
      nazev: parsed.rest,
      mj: toTrim(row.MJ),
      typpol: toTrim(row.TYP_POL),
      typradku: '1',
      hmotnost: toStr(row.HMOTNOST),
      pocet: toNum(row.POCETMJ).toFixed(2).replace('.', ','),
      cena: toNum(row.CENAMJ).toFixed(2).replace('.', ','),
      dph: toStr(toNum(row.SADZBADPH)),
      sleva: toEmptyIfZero(row.ZLAVA),
      slevamj: '',
      cenasdph: '',
      celkem: '',
      celkemsdph: '',
      vybavene: toStr(row.VYBAVENE),
      vybavit: toStr(row.VYBAVIT),
      uzvybmj: toStr(row.UZVYBMJ)
    })
    items.push(mappedRow)
  }

  const totals = items.length
    ? prepocitejCelkoveCastky(items)
    : {
      zaklad1: toFixed2(headerRow.ZAKL2),
      dph1: toFixed2(headerRow.DPH2),
      zaklad2: toFixed2(headerRow.ZAKL1),
      dph2: toFixed2(headerRow.DPH1),
      zaklad0: toFixed2(headerRow.ZAKL0),
      zaklad: toFixed2(toNum(headerRow.ZAKL0) + toNum(headerRow.ZAKL1) + toNum(headerRow.ZAKL2)),
      dph: toFixed2(toNum(headerRow.DPH1) + toNum(headerRow.DPH2)),
      celkem: toFixed2(headerRow.CELKOM),
      celkemsleva: toFixed2(0)
    }

  if (!items.length) {
    items.push({
      id: generateId(),
      idrkar: '', karta: '', nazev: '', mj: '', typpol: '', typradku: '1', hmotnost: '', pocet: '', cena: '', dph: '', sleva: '', slevamj: '', cenasdph: '', celkem: '', celkemsdph: '', vybavene: '', vybavit: '', uzvybmj: '0'
    })
  }

  const header = {
    idobj: toStr(headerRow.IDOBJ),
    cislo,
    stav: toTrim(headerRow.USRFLD3),
    vybavene: toStr(headerRow.VYBAVENE),
    datum: toTrim(formatDateForInput(headerRow.DATUM)),
    kredit: (() => {
      const kreditNum = toNum(headerRow.KREDIT)
      return kreditNum !== 0 ? kreditNum.toFixed(2) : ''
    })(),
    loguser: toTrim(headerRow.LOG_USER),
    cislostr: toTrim(headerRow.STREDISKO),
    stani: toTrim(headerRow.USRFLD2),
    uhrada: mapUhrada(headerRow.USRFLD4),
    skladovypohyb: toTrim(headerRow.USRFLD5),
    zakazka: toZakazkaString(headerRow.CISLO_ZAK),
    nplimit: '',
    npfakturace: '',
    idrbonus,
    bonus,
    stavbonus: '',
    bonusjmeno: toTrim(bonusjmeno),
    bonustelefon: toTrim(bonustelefon),
    bonusemail: toTrim(bonusemail),
    bonusadresa: toTrim(bonusadresa),
    spz: toTrim(headerRow.USRFLD1),
    origcislo: toTrim(headerRow.ORIGCISLO),
    poznamka: blobToText(headerRow.POZNAMKA),
    idradr: toStr(headerRow.IDRADR),
    ico: toTrim(headerRow.ICO),
    firma: toTrim(headerRow.FIRMA),
    ulice: toTrim(headerRow.ULICA),
    psc: toTrim(headerRow.PSC),
    mesto: toTrim(headerRow.MESTO),
    dic: toTrim(headerRow.DIC),
    telefon: toTrim(headerRow.TELEFON),
    email: toTrim(headerRow.EMAIL),
    adrpoznamka: blobToText(headerRow.APOZNAMKA),
    kpidradr: toStr(headerRow.KPIDRADR),
    icoprij: toTrim(headerRow.ICOPRIJ),
    kpfirma: toTrim(headerRow.KPFIRMA),
    kpulice: toTrim(headerRow.KPULICA),
    kppsc: toTrim(headerRow.KPPSC),
    kpmesto: toTrim(headerRow.KPMESTO),
    kpdic: toTrim(headerRow.KPDIC),
    kptelefon: toTrim(headerRow.KPTELEFON),
    kpemail: toTrim(headerRow.KPEMAIL),
    kpadrpoznamka: blobToText(headerRow.KPPOZNAMKA),
    zaklad1: toStr(totals.zaklad1),
    dph1: toStr(totals.dph1),
    zaklad2: toStr(totals.zaklad2),
    dph2: toStr(totals.dph2),
    zaklad0: toStr(totals.zaklad0),
    zaklad: toStr(totals.zaklad),
    dph: toStr(totals.dph),
    celkem: toStr(totals.celkem),
    celkemsleva: toStr(totals.celkemsleva)
  }
  return { header, items }
}
  
const OrderUpdate = () => {
  const location = useLocation()
  const orderStatus = location.state?.orderStatus || null
  const [header, setHeader] = useState({ datum: '', kredit: '', cislo: '', stav: '', vybavene: '', loguser: '', cislostr: '', stani: '', uhrada: '', skladovypohyb: '', zakazka: '', nplimit:'', npfakturace:'', idrbonus: '', bonus: '', stavbonus: '', bonusjmeno: '', bonustelefon: '', bonusemail: '', bonusadresa: '', spz: '', origcislo: '', poznamka: '', idradr: '', ico: '', firma: '', ulice: '', psc: '', mesto: '', dic: '', telefon:'', email:'', adrpoznamka:'', kpidradr:'', icoprij: '', kpfirma: '', kpulice: '', kppsc: '', kpmesto: '', kpdic:'', kptelefon:'', kpemail:'', kpadrpoznamka:'', zaklad1: '', dph1: '', zaklad2: '', dph2: '', zaklad0: '', zaklad: '', dph: '', celkem: '', celkemsleva: '' })
  const [items, setItems] = useState([{ id: generateId(), idrkar: '', karta: '', nazev: '', mj: '', typpol: '', typradku: '1', hmotnost: '', pocet: '', cena: '', dph: '', sleva: '', slevamj:'', cenasdph: '', celkem: '', celkemsdph: '', vybavene: '', vybavit: '', uzvybmj: '0' }])
  
  const [isLoadingOrder, setIsLoadingOrder] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [validatedOrder, setValidatedOrder] = useState(false)
  
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])

  const [strediska, setStrediska] = useState([])
  
  const [firmy, setFirmy] = useState([])
  const [search, setSearch] = useState('')
  const [showList, setShowList] = useState(false)

  const [kpfirmy, setFirmyKP] = useState([])
  const [kpsearch, setSearchKP] = useState('')
  const [showListKP, setShowListKP] = useState(false)

  const [searchZakazka, setSearchZakazka] = useState('')
  const [zakazky, setZakazky] = useState([])
  const [showListZakazky, setShowListZakazky] = useState(false)

  const [showAddAddressModal, setShowAddAddressModal] = useState(false)
  const [showEditAddressModal, setShowEditAddressModal] = useState(false)
  const [showAddAddressModalKP, setShowAddAddressModalKP] = useState(false)
  const [showEditAddressModalKP, setShowEditAddressModalKP] = useState(false)
  const [validatedAddress, setValidatedAddress] = useState(false)
  const [icoInput, setIcoInput] = useState('')
  const [newAddress, setNewAddress] = useState({})
  const [dphStatus, setDphStatus] = useState('')

  const [bonusKarty, setBonusKarty] = useState([])
  const [searchBonus, setSearchBonus] = useState('')
  const [showListBonus, setShowListBonus] = useState(false)

  const [showBonusModal, setShowBonusModal] = useState(false)
  const [mode, setMode] = useState('')
  const [validatedBonus, setValidatedBonus] = useState(false)
  const [newBonus, setNewBonus] = useState({})

  const [isError, setIsError] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const controllerRef = useRef(null)
  const timeoutRef = useRef(null)
  const wrapperRef = useRef(null)   // hlavní adresa
  const wrapperKPRef = useRef(null) // konečný příjemce
  const wrapperBKRef = useRef(null) // bonus karta
  const wrapperZKRef = useRef(null) // zakázka
  const orderFormRef = useRef(null)

  const [showGoodsModal, setShowGoodsModal] = useState(false)
  const [goodsSearch, setGoodsSearch] = useState('')
  const [goodsResults, setGoodsResults] = useState([])
  const [goodsLoading, setGoodsLoading] = useState(false)
  const [showNoGoods, setShowNoGoods] = useState(false)
  const [selectedGoods, setSelectedGoods] = useState(null)
  const [goodsQty, setGoodsQty] = useState(1)
  const [goodsPrice, setGoodsPrice] = useState('')
  const [goodsDiscount, setGoodsDiscount] = useState(0)
  const [goodsHistory, setGoodsHistory] = useState([])
  const [goodsHistoryLoading, setGoodsHistoryLoading] = useState(false)
  const [editingSlevaIndex, setEditingSlevaIndex] = useState(null) // edit mode pro slevu na řádku objednávky
  const [showClearItemsModal, setShowClearItemsModal] = useState(false) // modál pro vyčíštění všech položek

  const goodsControllerRef = useRef(null)
  const goodsTimeoutRef = useRef(null)
  const goodsReqIdRef = useRef(0)

  const [showServiceModal, setShowServiceModal] = useState(false)
  const [servicesAll, setServicesAll] = useState([])  // pole karet služeb          
  const [servicesLoaded, setServicesLoaded] = useState(false)
  const [serviceTyp, setServiceTyp] = useState('OS') // zvolený typ vozidla
  const [serviceSku, setServiceSku] = useState('STA')  // zvolená skupina
  const [serviceKat, setServiceKat] = useState('K1')
  const [activeService, setActiveService] = useState(null)  // aktuálně vybraná služby
  const [serviceQty, setServiceQty] = useState('1')   //
  const [serviceDiscount, setServiceDiscount] = useState('')

  const [showMaterialModal, setShowMaterialModal] = useState(false)
  const [materialAll, setMaterialAll] = useState([])          // pole karet materiálu
  const [materialLoaded, setMaterialLoaded] = useState(false)
  const [materialKat, setMaterialKat] = useState('ZÁVAŽÍ')         // zvolená kategorie
  const [activeMaterial, setActiveMaterial] = useState(null)  // vybraná karta materiálu
  const [materialQty, setMaterialQty] = useState('1')
  const [materialDiscount, setMaterialDiscount] = useState('')

  const isDark = document.documentElement.dataset.bsTheme === 'dark'

  // Načtení limitu NP a čerpání pro vybranou zakázku
  const fetchNPValues = async (cisloZak, fallbackLimit = '') => {
    const cislo = String(cisloZak ?? '').trim()
    if (!cislo.toUpperCase().startsWith('NP')) {
      return { nplimit: '', npfakturace: '' }
    }
    let nplimit = String(fallbackLimit ?? '').trim()
    let npfakturace = '0'
    try {
      const rows = await apiFetch(`/mrp/zakazky?hodnota=${encodeURIComponent(cislo)}`)
      const exact = (Array.isArray(rows) ? rows : []).find(
        (z) => String(z?.CISLO ?? '').trim().toUpperCase() === cislo.toUpperCase()
      )
      if (exact) nplimit = String(exact?.SUMA ?? '').trim()
    } catch (err) {
      console.error('Chyba při načítání limitu NP:', err)
    }
    try {
      const data = await apiFetch(`/mrp/cerpaniNP?cislo=${encodeURIComponent(cislo)}`)
      npfakturace = String(data?.castka ?? '0')
    } catch (err) {
      console.error('Chyba při načítání čerpání NP:', err)
      npfakturace = '0'
    }
    return { nplimit, npfakturace }
  }

  const fetchBonusStav = async (idrbonus) => {
    const id = String(idrbonus ?? '').trim()
    if (!id) return ''
    try {
      const data = await apiFetch(`/mrp/stavbonus?id=${encodeURIComponent(id)}`)
      return String(data?.stav ?? '0')
    } catch (err) {
      console.error('Chyba při načítání stavu bonus karty:', err)
      return '0'
    }
  }

  // Načtení objednávky z MRP
  useEffect(() => {
    const idobj = location.state?.idobj
    if (!idobj) {
      setLoadError('Chybí ID objednávky.')
      setIsLoadingOrder(false)
      return
    }
    let isMounted = true
    const load = async () => {
      try {
        const data = await apiFetch(`/mrp/nactiobjednavka?idobj=${encodeURIComponent(idobj)}`)
        const mapped = mapOrderToForm(data)
        const [npValues, stavbonus] = await Promise.all([
          fetchNPValues(mapped?.header?.zakazka || ''),
          fetchBonusStav(mapped?.header?.idrbonus || '')
        ])
        if (!isMounted) return
        setHeader(prev => ({ ...prev, ...mapped.header, ...npValues, stavbonus }))
        setItems(mapped.items)
        setSearch(mapped.header.firma || '')
        setSearchKP(mapped.header.kpfirma || '')
        setSearchZakazka(mapped.header.zakazka || '')
        setSearchBonus(mapped.header.bonus || '')
        await fetchLogUser()
      } catch (error) {
        if (!isMounted) return
        setLoadError(error?.message || 'Nepodařilo se načíst objednávku.')
      } finally {
        if (isMounted) setIsLoadingOrder(false)
      }
    }
    load()
    return () => {
      isMounted = false
    }
  }, [location.state])

  // Po načtení stránky
  useEffect(() => {
    fetchStrediska()
  }, [])

  useEffect(() => {
    if (!strediska.length) return
    const selected = strediska[0]
    if (!selected) return
    const nextCislostr = selected.cislo ?? ''
    const staniCount = Number(selected.stani) || 0
    const currentStani = Number(header.stani)
    const nextStani =
      Number.isFinite(currentStani) && currentStani >= 1 && currentStani <= staniCount
        ? String(currentStani)
        : '1'
    if (String(header.cislostr) === String(nextCislostr) && String(header.stani) === String(nextStani)) {
      return
    }
    setHeader(prev => ({
      ...prev,
      cislostr: String(nextCislostr),
      stani: nextStani
    }))
  }, [strediska, header.cislostr, header.stani])

  // Přepočítání celkových částek vždy při změně položky
  useEffect(() => {
    const noveCelkove = prepocitejCelkoveCastky(items)
    setHeader(prev => ({ ...prev, ...noveCelkove }))
  }, [items]) 

  // Aktualizace slev podle bonus karty (30% na služby 90000–91000)
  useEffect(() => {
    setItems(prevItems => {
      const hasBonus = !!String(header?.bonus ?? '').trim()
      let changed = false
      const next = prevItems.map(item => {
        const kartaNum = Number(String(item?.karta ?? '').replace(',', '.')) || 0
        const isService = kartaNum >= 90000 && kartaNum <= 91000
        if (!isService) return item
        const nextSleva = hasBonus ? '30' : ''
        const currSleva = String(item?.sleva ?? '')
        if (currSleva !== nextSleva) {
          changed = true
          return recalcRow({ ...item, sleva: nextSleva })
        }
        return item
      })
      return changed ? next : prevItems
    })
  }, [header?.bonus])

  // Načtení středisek
  const fetchStrediska = async () => {
    try {
      const list = await apiFetch('/strediska')
      setStrediska(list)
      //console.log('strediska:', list)
    } catch (error) {
      console.error('Chyba při načítání dat:', error)
    }
  }

  const fetchLogUser = async () => {
    try {
      const data = await apiFetch('/uzivatele/loguser')
      setHeader(prev => ({ ...prev, loguser: data?.uzivatel_mrp || '' }))
    } catch (error) {
      console.error('Chyba při načítání uzivatel_mrp:', error)
    }
  }
  
  /* ------------------------------
      === HLAVNÍ ADRESA ===
  -------------------------------*/
  // Handle pro změny v poli hledání hlavní adresy
  const handleChange = (e) => {
    const value = e.target.value
    setSearch(value)
    searchMRPFirmy(value)
    setHeader((prev) => ({
      ...prev,
      idradr: '',
      kredit: '',
      ico: '',
      dic: '',
      firma: '',
      ulice: '',
      psc: '',
      mesto: '',
      telefon: '',
      email: '',
      adrpoznamka: ''
    }))
  }

  // Hledání firmy v MRP
  const searchMRPFirmy = async (input) => {
    if (input.length < 3) {
      setFirmy([])
      setShowList(false)
      return
    }
    // zruš předchozí timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(async () => {
      // zruš předchozí request
      if (controllerRef.current) {
        controllerRef.current.abort()
      }
      controllerRef.current = new AbortController()
      const signal = controllerRef.current.signal
      const normalizedInput = input.replace(/\s+/g, '').toUpperCase()
      const isICO = /^[A-Z]?\d+$/.test(normalizedInput)
      try {
        const params = new URLSearchParams()
        if (isICO) {
          params.append('ico', normalizedInput)  // čisté IČ, velká písmena, bez mezer
        } else {
          params.append('firma', input)  // název firmy necháme jak uživatel zadal
        }
        const data = await apiFetch(
          `/mrp/firmy?${params.toString()}`,
          { signal }
        )
        //console.log(data)
        setFirmy(data)
        setShowList(true)
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Zrušený požadavek')
        } else {
          console.error('Chyba při načítání adresy:', err)
        }
        setShowList(false)
      }      
    }, 400) // debounce 400 ms
  }

  // Handle pro výběr firmy z datalistu vyhledaných firem
  const handleSelect = (firma) => {
    const kreditNum = Number(String(firma?.KREDIT ?? '').replace(',', '.'))
    setHeader((prev) => ({
      ...prev,
      idradr: String(firma.IDRADR) || '',
      kredit: Number.isFinite(kreditNum) && kreditNum !== 0 ? kreditNum.toFixed(2) : '',
      ico: firma.ICO || '',
      dic: firma.DIC || '',
      firma: firma.FIRMA || '',
      ulice: firma.ULICA || '',
      psc: firma.PSC || '',
      mesto: firma.MESTO || '',
      telefon: firma.TELEFON || '',
      email: firma.EMAIL || '',
      adrpoznamka: firma.POZNAMKA || ''
    }))
    setSearch(firma.FIRMA)
    setShowList(false)
  } 

  // ARES dotaz – nové REST API
  const handleIcoChange = async (e) => {
    const value = e.target.value.toUpperCase()
    setIcoInput(value)
    setIsError(false)
    setErrorMsg('')
    setDphStatus('')
    if (/^\d{8}$/.test(value)) {
      try {
        const response = await fetch(
          `https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/${value}`
        )
        // u 404 zkusíme přečíst JSON, u ostatních kódů vyhodíme chybu
        if (!response.ok && response.status !== 404) {
          throw new Error(`ARES API chyba: ${response.status}`)
        }
        const data = await response.json()
        //console.log('ARES response:', data)
        if (data.kod === 'NENALEZENO') {
          setNewAddress({})
          setIsError(true)
          setErrorMsg('Subjekt s tímto IČ nebyl v ARES nalezen.')
          return
        }
        const ulice = data.sidlo?.nazevUlice
          ? `${data.sidlo.nazevUlice} ${data.sidlo?.cisloDomovni || ''}`.trim()
          : `${data.sidlo?.nazevObce || ''} ${data.sidlo?.cisloDomovni || ''}`.trim()
        const newAddr = {
          idradr: String(data.idradr) || '',
          ico: data.ico || '',
          dic: data.dic || '',
          firma: data.obchodniJmeno || '',
          ulice,
          psc: data.sidlo?.psc?.toString() || '',
          mesto: data.sidlo?.nazevObce || ''
        }
        setNewAddress(newAddr)
        // pokud je DIC k dispozici, ověříme spolehlivost plátce přes backend
        if (newAddr.dic) {
          try {
            const dphData = await apiFetch(
              `/mrp/dph-status?dic=${newAddr.dic}`
            )
            console.log('DPH status raw:', dphData.raw)

            // Zpracování stavu plátce z raw.statusPlatceDPH
            const platce = dphData.raw.statusPlatceDPH?.[0]?.attributes?.nespolehlivyPlatce
            //console.log('Nespolehlivý plátce:', platce) 
            setDphStatus(platce)
          } catch (dphErr) {
            console.error('Chyba při ověření spolehlivosti plátce:', dphErr)
            setDphStatus({ error: `Chyba ověření DPH: ${dphErr.message}` })
          }
        }
      } catch (err) {
        console.error('Chyba při načítání z ARES:', err)
        setNewAddress({})
        setIsError(true)
        setErrorMsg(`Chyba spojení s ARES: ${err.message || err.toString()}`)
      }
    } else {
      setNewAddress({})
    }
  }

  // Uložení nové adresy
  const handleAddAddress = async (e) => {
    e.preventDefault()    
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidatedAddress(true)
    if (!isValid) {
      return
    }
    const formData = new FormData(form)
    const firma = formData.get('firma')    
    const ico = formData.get('ico')    
    const dic = newAddress.dic || ''
    const ulice = formData.get('ulice')    
    const psc = formData.get('psc')    
    const mesto = formData.get('mesto')    
    const predvolba = formData.get('predvolba')
    const telefon = formData.get('telefon')    
    const email = formData.get('email')
    const celeCislo = telefon ? `${predvolba}${telefon}` : ''
    const poznamka = formData.get('poznamka')
    const newItem = { firma, ico, dic, ulice, psc, mesto, email, telefon:celeCislo, poznamka }
    //console.log('nová adresa: ', newItem)
    try {
      const result = await apiFetch('/mrp/novaadresa', {
        method: 'POST',
        body: JSON.stringify(newItem),
      })
      setHeader((prev) => ({
        ...prev,
        idradr: result?.id ? String(result.id) : '',
        kredit: '',
        ico: result.ico || '',
        dic: dic || '',
        firma: firma || '',
        ulice: ulice || '',
        psc: psc || '',
        mesto: mesto || '',
        telefon: celeCislo || '',
        email: email || '',
        adrpoznamka: poznamka || ''
      }))
      setSearch(firma)
      closeModal() 
      const newNotification = {
        id: Date.now(),
        type: 'success',
        message: `Nová adresa byla uložena.`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])    
    } catch (err) {
      console.error('Chyba při ukládání záznamu:', err)
      setIsError(true)
      setErrorMsg(err.message || err.toString())
    }
  }

  // Uložení změny adresy
  const handleEditAddress = async (e) => {
    e.preventDefault()    
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidatedAddress(true)
    if (!isValid) {
      return
    }
    const formData = new FormData(form)
    const idradr = header.idradr
    const firma = formData.get('firma')        
    const ulice = formData.get('ulice')    
    const psc = formData.get('psc')    
    const mesto = formData.get('mesto')    
    const telefon = formData.get('telefon') || ''    
    const email = formData.get('email') || ''
    const poznamka = formData.get('poznamka') || ''
    const editItem = { idradr, firma, ulice, psc, mesto, telefon, email, poznamka }
    try {
      await apiFetch('/mrp/zmenaadresa', {
        method: 'PUT',
        body: JSON.stringify(editItem),
      })
      setHeader((prev) => ({
        ...prev,
        firma: firma || '',
        ulice: ulice || '',
        psc: psc || '',
        mesto: mesto || '',
        telefon: telefon || '',
        email: email || '',
        adrpoznamka: poznamka || ''
      }))
      setSearch(firma)
      closeModal() 
      const newNotification = {
        id: Date.now(),
        type: 'success',
        message: `Adresa byla upravena.`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])    
    } catch (err) {
      console.error('Chyba při ukládání záznamu:', err)
      setIsError(true)
      setErrorMsg(err.message || err.toString())
    }
  }

  /* ------------------------------
    === KONEČNÝ PŘÍJEMCE ===
  -------------------------------*/
  // Handle pro změny v poli hledání konečného příjemce
  const handleChangeKP = (e) => {
    const value = e.target.value
    setSearchKP(value)
    searchMRPFirmyKP(value)
    setHeader((prev) => ({
      ...prev,
      kpidradr: '',
      icoprij: '',
      kpdic: '',
      kpfirma: '',
      kpulice: '',
      kppsc: '',
      kpmesto: '',
      kptelefon: '',
      kpemail: '',
      kpadrpoznamka: ''
    }))
  }

  // Hledání konečného příjemce v MRP
  const searchMRPFirmyKP = async (input) => {
    if (input.length < 3) {
      setFirmyKP([])
      setShowListKP(false)
      return
    }
    // zruš předchozí timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(async () => {
      // zruš předchozí request
      if (controllerRef.current) {
        controllerRef.current.abort()
      }
      controllerRef.current = new AbortController()
      const signal = controllerRef.current.signal
      const normalizedInput = input.replace(/\s+/g, '').toUpperCase()
      const isICO = /^[A-Z]?\d+$/.test(normalizedInput)
      try {
        const params = new URLSearchParams()
        if (isICO) {
          params.append('ico', normalizedInput)  // čisté IČ, velká písmena, bez mezer
        } else {
          params.append('firma', input)  // název firmy necháme jak uživatel zadal
        }
        const data = await apiFetch(`/mrp/firmyKP?${params.toString()}`, { signal })
        setFirmyKP(data)
        setShowListKP(true)
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Zrušený požadavek')
        } else {
          console.error('Chyba při načítání adresy:', err)
        }
        setShowListKP(false)
      }      
    }, 400) // debounce 400 ms
  }

   // Handle pro výběr konečného příjemce z datalistu vyhledaných firem
  const handleSelectKP = (firma) => {
    setHeader((prev) => ({
      ...prev,
      kpidradr: String(firma.IDRADR) || '',
      icoprij: firma.ICO || '',
      kpdic: firma.DIC || '',
      kpfirma: firma.FIRMA || '',
      kpulice: firma.ULICA || '',
      kppsc: firma.PSC || '',
      kpmesto: firma.MESTO || '',
      kptelefon: firma.TELEFON || '',
      kpemail: firma.EMAIL || '',
      kpadrpoznamka: firma.POZNAMKA || ''
    }))
    setSearchKP(firma.FIRMA)
    setShowListKP(false)
  }

  // Uložení nové adresy
  const handleAddAddressKP = async (e) => {
    e.preventDefault()    
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidatedAddress(true)
    if (!isValid) {
      return
    }
    const formData = new FormData(form)
    const firma = formData.get('firma')    
    const ico = formData.get('ico')    
    const dic = formData.get('dic')
    const ulice = formData.get('ulice')    
    const psc = formData.get('psc')    
    const mesto = formData.get('mesto')    
    const predvolba = formData.get('predvolba')
    const telefon = formData.get('telefon')    
    const email = formData.get('email')
    const celeCislo = telefon ? `${predvolba}${telefon}` : ''
    const poznamka = formData.get('poznamka')
    const newItem = { firma, ico, dic, ulice, psc, mesto, email, telefon:celeCislo, poznamka }
    //console.log('nová adresa: ', newItem)
    try {
      const result = await apiFetch('/mrp/novaadresa', {
        method: 'POST',
        body: JSON.stringify(newItem),
      })
      setHeader((prev) => ({
        ...prev,
        kpidradr: result?.id ? String(result.id) : '',
        icoprij: result.ico || '',
        kpdic: dic || '',
        kpfirma: firma || '',
        kpulice: ulice || '',
        kppsc: psc || '',
        kpmesto: mesto || '',
        kptelefon: celeCislo || '',
        kpemail: email || '',
        kpadrpoznamka: poznamka || ''
      }))
      setSearchKP(firma)
      closeModal() 
      const newNotification = {
        id: Date.now(),
        type: 'success',
        message: `Nová adresa byla uložena.`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])    
    } catch (err) {
      console.error('Chyba při ukládání záznamu:', err)
      setIsError(true)
      setErrorMsg(err.message || err.toString())
    }
  }

  // Uložení změny adresy konečného příjemce
  const handleEditAddressKP = async (e) => {
    e.preventDefault()    
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidatedAddress(true)
    if (!isValid) {
      return
    }
    const formData = new FormData(form)
    const idradr = header.kpidradr
    const firma = formData.get('kpfirma')        
    const ulice = formData.get('kpulice')    
    const psc = formData.get('kppsc')    
    const mesto = formData.get('kpmesto')    
    const telefon = formData.get('kptelefon') || ''    
    const email = formData.get('kpemail') || ''
    const poznamka = formData.get('kppoznamka') || ''
    const editItem = { idradr, firma, ulice, psc, mesto, telefon, email, poznamka }
    try {
      await apiFetch('/mrp/zmenaadresa', {
        method: 'PUT',
        body: JSON.stringify(editItem),
      })
      setHeader((prev) => ({
        ...prev,
        kpfirma: firma || '',
        kpulice: ulice || '',
        kppsc: psc || '',
        kpmesto: mesto || '',
        kptelefon: telefon || '',
        kpemail: email || '',
        kpadrpoznamka: poznamka || ''
      }))
      setSearchKP(firma)
      closeModal() 
      const newNotification = {
        id: Date.now(),
        type: 'success',
        message: `Adresa byla upravena.`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])    
    } catch (err) {
      console.error('Chyba při ukládání záznamu:', err)
      setIsError(true)
      setErrorMsg(err.message || err.toString())
    }
  }

  /* ------------------------------
      === BONUS KARTA ===
  -------------------------------*/
  // Handle pro změny v poli hledání bonus karty
  const handleChangeBonus = (e) => {
    const value = e.target.value
    setSearchBonus(value)
    searchMRPBonus(value)
    setHeader((prev) => ({
      ...prev,
      idrbonus: '',
      bonus: '',
      stavbonus: '',
      bonusjmeno: '',
      bonustelefon: '',
      bonusemail: '',
      bonusadresa: ''
    }))
  }

  // Hledání bonus karet v MRP
  const searchMRPBonus = async (input) => {
    if (input.length < 3) {
      setBonusKarty([])
      setShowListBonus(false)
      return
    }
    // zruš předchozí timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(async () => {
      // zruš předchozí request
      if (controllerRef.current) {
        controllerRef.current.abort()
      }
      controllerRef.current = new AbortController()
      const signal = controllerRef.current.signal
      try {
        const params = new URLSearchParams()
        params.append('hodnota', input)
        const data = await apiFetch(
          `/mrp/bonus?${params.toString()}`,
          { signal }
        )
        setBonusKarty(data)
        setShowListBonus(true)
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Zrušený požadavek')
        } else {
          console.error('Chyba při načítání bonus karty:', err)
        }
        setShowListBonus(false)
      }      
    }, 400) // debounce 400 ms
  }

  // Výběr bonus karty z datalistu
  const handleSelectBonus = (karta) => {
    setHeader((prev) => ({
      ...prev,
      idrbonus: String(karta.IDR) || '',
      bonus: String(karta.CISLO) || '',
      bonusjmeno: String(karta.JMENO) || '',
      bonustelefon: String(karta.TELEFON) || '',
      bonusemail: String(karta.EMAIL) || '',
      bonusadresa: String(karta.ADRESA) || ''
    }))
    setSearchBonus(karta.CISLO)
    setShowListBonus(false)
    if (karta?.IDR) findMRPBonusStav(karta.IDR)
  }

  // Zjisti stav bodů vybrané bonus karty
  const findMRPBonusStav = async (id) => {
    const stav = await fetchBonusStav(id)
    setHeader((prev) => ({ ...prev, stavbonus: stav }))
  }

  // Uložení nové bonus karty
  const handleAddBonus = async (e) => {
    e.preventDefault()    
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidatedBonus(true)
    if (!isValid) {
      return
    }
    const formData = new FormData(form)
    const cislo = formData.get('cislo')    
    const jmeno = formData.get('jmeno') 
    const predvolba = formData.get('predvolba')
    const telefon = formData.get('telefon')    
    const celeCislo = `${predvolba}${telefon}`   
    const email = formData.get('email')    
    const adresa = formData.get('adresa')    
    const newItem = { cislo, jmeno, celeCislo, email, adresa }
    //console.log('nová bonus karta: ', newItem)
    try {
      const result = await apiFetch('/mrp/newbonus', {
        method: 'POST',
        body: JSON.stringify(newItem),
      })
      setHeader((prev) => ({
        ...prev,
        idrbonus: String(result.idr || ''),
        bonus: String(cislo || ''),
        bonusjmeno: String(jmeno || ''),
        bonustelefon: String(celeCislo || ''),
        bonusemail: String(email || ''),
        bonusadresa: String(adresa || '')
      }))
      setSearchBonus(cislo)
      closeModal() 
      const newNotification = {
        id: Date.now(),
        type: 'success',
        message: `Nová bonus karta byla uložena.`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])    
    } catch (err) {
      console.error('Chyba při ukládání záznamu:', err)
      setIsError(true)
      setErrorMsg(err.message || err.toString())
    }
  }

  // Uložení změn existující bonus karty
  const handleEditBonus = async (e) => {
    e.preventDefault()    
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidatedBonus(true)
    if (!isValid) {
      return
    }
    const formData = new FormData(form)
    const jmeno = formData.get('jmeno') 
    const predvolba = formData.get('predvolba')
    const telefon = formData.get('telefon')    
    const celeCislo = `${predvolba}${telefon}`   
    const email = formData.get('email')    
    const adresa = formData.get('adresa')    
    const updateItem = { 
      idr: header.idrbonus,  
      cislo: header.bonus,
      jmeno,
      celeCislo,
      email,
      adresa
    }
    //console.log('editace bonus karty: ', updateItem)
    try {
      const result = await apiFetch('/mrp/editbonus', {
        method: 'PUT',
        body: JSON.stringify(updateItem),
      })
      setHeader((prev) => ({
        ...prev,
        idrbonus: String(result.idr || prev.idrbonus || ''),
        bonus: String(result.cislo || prev.bonus || ''),
        bonusjmeno: String(jmeno || ''),
        bonustelefon: String(celeCislo || ''),
        bonusemail: String(email || ''),
        bonusadresa: String(adresa || '')
      }))
      setSearchBonus(String(result.cislo || header.bonus || ''))
      closeModal()
      const newNotification = {
        id: Date.now(),
        type: 'success',
        message: `Bonus karta byla upravena.`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])    
    } catch (err) {
      console.error('Chyba při úpravě záznamu:', err)
      setIsError(true)
      setErrorMsg(err.message || err.toString())
    }
  }
  
  /* ------------------------------
      === HLAVIČKA DOKLADU ===
  -------------------------------*/
  // Handle pro změny ve formuláři hlavičky dokladu
  const handleHeaderChange = (e) => {
    const { name, value } = e.target
    setHeader(prev => ({ ...prev, [name]: String(value) }))
  }

  const clearCompany = () => {
    setSearch(''); setFirmy([]); setShowList(false)
    setHeader((prev) => ({ ...prev, idradr: '', ico: '', firma: '', ulice: '', psc: '', mesto: '', dic: '', telefon: '', email: '', adrpoznamka: '' }))
  }
  const clearFinalRecipient = () => {
    setSearchKP(''); setFirmyKP([]); setShowListKP(false)
    setHeader((prev) => ({ ...prev, kpidradr: '', icoprij: '', kpfirma: '', kpulice: '', kppsc: '', kpmesto: '', kpdic: '', kptelefon: '', kpemail: '', kpadrpoznamka: '' }))
  }
  const clearBonusCard = () => {
    setSearchBonus(''); setBonusKarty([]); setBonusKarta({}); setShowListBonus(false)
    setHeader((prev) => ({ ...prev, idrbonus: '', bonus: '', stavbonus: '' }))
  }
  const clearJob = () => {
    setSearchZakazka(''); setZakazky([]); setShowListZakazky(false)
    setHeader((prev) => ({ ...prev, zakazka: '', nplimit: '', npfakturace: '' }))
  }

  // Handle pro změny v poli hledání hlavní adresy
  const handleZakazka = (e) => {
    const value = e.target.value
    setSearchZakazka(value)
    searchMRPZakazky(value)
    setHeader((prev) => ({
      ...prev,
      zakazka: '',
      nplimit: '',
      npfakturace: ''
    }))
  }

  // Hladání v zakázkách MRP
  const searchMRPZakazky = async (input) => {
    if (input.length < 3) {
      setZakazky([])
      setShowListZakazky(false)
      return
    }
    // zruš předchozí timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(async () => {
      // zruš předchozí request
      if (controllerRef.current) {
        controllerRef.current.abort()
      }
      controllerRef.current = new AbortController()
      const signal = controllerRef.current.signal
      try {
        const params = new URLSearchParams()
        params.append('hodnota', input)
        const data = await apiFetch(
          `/mrp/zakazky?${params.toString()}`,
          { signal }
        )
        setZakazky(data)
        setShowListZakazky(true)
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Zrušený požadavek')
        } else {
          console.error('Chyba při načítání bonus karty:', err)
        }
        setShowListZakazky(false)
      }      
    }, 400) // debounce 400 ms
  }

  // Handle pro výběr zakázky z datalistu
  const handleSelectZakazka = async (zakazka) => {
    const cislo = String(zakazka?.CISLO || '')
    const npValues = await fetchNPValues(cislo, zakazka?.SUMA || '')
    setHeader((prev) => ({
      ...prev,
      zakazka: cislo,
      ...npValues
    }))
    setSearchZakazka(cislo)
    setShowListZakazky(false)
  }

  /* ------------------------------
      === VÝBĚR ZBOŽÍ ===
  -------------------------------*/
  // načítání zboží
  const searchGoods = (value) => {
    const v = (value ?? '').toString()
    // 1) zruš debounce timer
    if (goodsTimeoutRef.current) clearTimeout(goodsTimeoutRef.current)
    // 2) když je to krátké / prázdné → hned čistit a abortnout request
    if (v.trim().length < 3) {
      if (goodsControllerRef.current) goodsControllerRef.current.abort()
      setGoodsResults([])
      setGoodsLoading(false)
      return
    }
    // 3) debounce
    goodsTimeoutRef.current = setTimeout(async () => {
      // abort předchozího requestu
      if (goodsControllerRef.current) goodsControllerRef.current.abort()
      const controller = new AbortController()
      goodsControllerRef.current = controller
      // requestId proti doběhnutí staré odpovědi
      const reqId = ++goodsReqIdRef.current
      setGoodsLoading(true)
      try {
        const activeSklad = strediska?.[0]?.sklad_mrp
        const sklad = activeSklad ?? 4
        const data = await apiFetch(
          `/mrp/zbozi?query=${encodeURIComponent(v.trim())}&sklad=${encodeURIComponent(sklad)}`,
          { signal: controller.signal }
        )
        // ignoruj stale odpověď
        if (reqId !== goodsReqIdRef.current) return
        setGoodsResults((Array.isArray(data) ? data : []).slice(0, 50))
      } catch (e) {
        if (e?.name === 'AbortError') return
        console.error('Chyba hledání zboží:', e)
      } finally {
        if (reqId === goodsReqIdRef.current) setGoodsLoading(false)
      }
    }, 300)
  }  
  // už vybráno 
  const selectedGoodsCards = useMemo(() => {
    const set = new Set()
    items.forEach(i => { if (i?.karta) set.add(String(i.karta)) })
    return set
  }, [items])
  // bloky
  const isGoodsAlreadyInOrder = (g) => selectedGoodsCards.has(String(g?.karta)) 
  const isSelectedGoodsAlreadyInOrder = !!selectedGoods && isGoodsAlreadyInOrder(selectedGoods)
  const hasSearch = goodsSearch.trim().length >= 3 // flag pro rozlišení „ještě nehledám“ × „nenašli jsme nic“
  // vložení položky do objednávky
  const addGoodsToItems = () => {
    if (!selectedGoods) return
    if (isGoodsAlreadyInOrder(selectedGoods)) return
    const hasBonus = !!String(header?.bonus ?? '').trim()
    const kartaNum = Number(String(selectedGoods?.karta ?? '').replace(',', '.')) || 0
    const isBonusService = kartaNum >= 90000 && kartaNum <= 91000
    const slevaFinal = (hasBonus && isBonusService) ? 30 : toNum(goodsDiscount)
    const slevaStr = (hasBonus && isBonusService) ? '30' : (goodsDiscount ? String(goodsDiscount) : '')
    const newItemRaw = {
      id: generateId(),
      idrkar: String(selectedGoods.id),
      karta: selectedGoods.karta,                               // už "123,00"
      nazev: selectedGoods.nazev,
      mj: selectedGoods.mj,
      typpol: selectedGoods.typpol,
      typradku: '1',
      hmotnost: (Number(selectedGoods.hmotnost ?? 0)).toFixed(4).replace('.', ','),
      pocet: (Number(goodsQty ?? 0)).toFixed(2).replace('.', ','),          // "1,00"
      cena: (Number(goodsPrice !== '' ? goodsPrice : (selectedGoods.cena ?? 0))).toFixed(2).replace('.', ','),
      sleva: slevaStr,                                          // '' nebo '10'
      dph: String(selectedGoods.dph ?? ''),                     // '21' / '12' / '0'
      cenasdph: '',
      celkem: '',
      celkemsdph: '',
      vybavene: '',
      vybavit: '',
      uzvybmj: '0',
    }
    // dopočet hned při vložení 
    const pocet = toNum(newItemRaw.pocet)
    const cena = toNum(newItemRaw.cena)
    const slevamj = round2(cena * (slevaFinal / 100))
    const dph = toNum(newItemRaw.dph)
    const cenasdph = round2(cena * (1 + dph / 100))
    const celkem = round2(pocet * cena * (1 - slevaFinal / 100))
    const celkemsdph = round2(celkem * (1 + dph / 100))
    const newItem = recalcRow({
      ...newItemRaw,
      slevamj: slevamj.toFixed(2),
      cenasdph: cenasdph.toFixed(2),
      celkem: celkem.toFixed(2),
      celkemsdph: celkemsdph.toFixed(2),
    })
    setItems((prev) => {
      const isFirstEmpty =
        prev.length === 1 &&
        String(prev[0]?.typradku ?? '1') === '1' &&
        !prev[0].karta &&
        !prev[0].nazev
      return isFirstEmpty ? [newItem] : [...prev, newItem]
    })
  }
  // vyčístění při zavření modálu
  const closeGoodsModal = () => {
    if (goodsTimeoutRef.current) clearTimeout(goodsTimeoutRef.current)
    if (goodsControllerRef.current) goodsControllerRef.current.abort()
    goodsReqIdRef.current += 1 // zneplatni případnou odpověď
    setGoodsResults([])
    setGoodsSearch('')
    setSelectedGoods(null)
    setGoodsPrice('')
    setShowGoodsModal(false)
    setGoodsLoading(false)
    setShowNoGoods(false)
  }
  
  // vyčištění výběru v datalistu při změně v poli hledání
  useEffect(() => {
    if (!selectedGoods) return
    const stillExists = goodsResults.some(g => g.karta === selectedGoods.karta)
    if (!stillExists) setSelectedGoods(null)
  }, [goodsResults]) 

  useEffect(() => {
    if (!selectedGoods) {
      setGoodsHistory([])
      setGoodsHistoryLoading(false)
      return
    }
    const ico = String(header?.ico ?? '').trim()
    const karta = String(selectedGoods?.karta ?? '').trim().replace(',', '.')
    const sklad = Number(strediska?.[0]?.sklad_mrp ?? 0)
    if (!ico || !karta || !Number.isFinite(sklad) || sklad <= 0) {
      setGoodsHistory([])
      setGoodsHistoryLoading(false)
      return
    }
    let canceled = false
    ;(async () => {
      setGoodsHistoryLoading(true)
      try {
        const data = await apiFetch(`/mrp/historieprodeje?ico=${encodeURIComponent(ico)}&sklad=${encodeURIComponent(sklad)}&cislokar=${encodeURIComponent(karta)}`)
        if (!canceled) setGoodsHistory(Array.isArray(data) ? data : [])
      } catch (e) {
        if (!canceled) {
          console.error('Chyba načítání historie prodeje:', e)
          setGoodsHistory([])
        }
      } finally {
        if (!canceled) setGoodsHistoryLoading(false)
      }
    })()
    return () => { canceled = true }
  }, [selectedGoods, header?.ico, strediska])

  // zpoždění hlášky když není nalezeno žádné zboží
  useEffect(() => {
    // reset při psaní nebo loadingu
    if (!hasSearch || goodsLoading) {
      setShowNoGoods(false)
      return
    }
    if (goodsResults.length === 0) {
      const t = setTimeout(() => {
        setShowNoGoods(true)
      }, 800) // ⬅ zpoždění (klidně 400–700 ms)
      return () => clearTimeout(t)
    }
    // pokud jsou výsledky, hlášku skryj
    setShowNoGoods(false)
  }, [hasSearch, goodsResults, goodsLoading])

  /* ------------------------------
      === VÝBĚR SLUŽBY ===
  -------------------------------*/
  const SERVICE_GROUPS = {
    OS: ['STA','ALU','VAN','SUV','RFT'],
    NA: ['SIL','ZEM','EM'],
    MO: [],   // jen ALL se přimíchá automaticky
    MN: [],
  }
  const SERVICE_TYPES = [
    { code: 'OS', icon: 'bi-car-front-fill', label: 'osobní' },
    { code: 'NA', icon: 'bi-truck-front-fill', label: 'nákladní' },
    { code: 'MO', icon: 'bi-car-front-fill', label: 'm.osobní' },
    { code: 'MN', icon: 'bi-truck-front-fill', label: 'm.nákladní' },
  ]
  const SERVICE_GROUPS_LABELS = [
    { code: 'STA', label: '13"-15"' },
    { code: 'ALU', label: '16"-17"' },
    { code: 'VAN', label: 'dodávky' },
    { code: 'SUV', label: '18"-19"' },
    { code: 'RFT', label: '20"-21"' },
    { code: 'SIL', label: 'silnice' },
    { code: 'ZEM', label: 'traktor' },
    { code: 'EM', label: 'stroje' },
  ]
  const SERVICE_CATS = [
    { code: 'K1', icon: 'bi-rocket', label: 'komplety, de/montáž, vyvážení' },
    { code: 'K2', icon: 'bi-gear', label: 'opravy, ventilky a ostatní práce' },
    { code: 'K3', icon: 'bi-water', label: 'čištění' },
    { code: 'K4', icon: 'bi-speedometer2', label: 'huštění a TMPS' },
    { code: 'K5', icon: 'bi-signpost-2', label: 'výjezdy' },
    { code: 'K6', icon: 'bi-boxes', label: 'uskladnění' },
  ]
  // helper pro popisky skupin
  const getGroupLabel = (code) => SERVICE_GROUPS_LABELS.find(x => x.code === code)?.label || code
  // Načtení karet služeb po otevření modálu 
  useEffect(() => {
    if (!showServiceModal || servicesLoaded) return
    const load = async () => {
      try {
        const data = await apiFetch(`/mrp/sluzby`)  
        setServicesAll(Array.isArray(data) ? data : [])
        setServicesLoaded(true)
      } catch (e) {
        console.error('Načtení služeb selhalo:', e)
        setServicesAll([])
      }
    }
    load()
  }, [showServiceModal, servicesLoaded])
  // výběr zobrazených služeb podle nastavených filtrů serviceTyp a serviceSku
  const filteredServices = useMemo(() => {
    return servicesAll.filter(s =>
      s.typ === serviceTyp &&
      (s.sku === serviceSku || s.sku === 'ALL') // ALL se přimíchá vždy
    )
  }, [servicesAll, serviceTyp, serviceSku])
  // nastavení defaultní skupiny při změně typu vozidla (je to první v poli SERVICE_GROUPS)
  const handleTypChange = (t) => {
    setServiceTyp(t)
    const groups = SERVICE_GROUPS[t] || []
    setServiceSku(groups[0] ?? '')  // MO/MN -> '' (skupiny nevybíráš)
    setServiceKat('K1')
    setActiveService(null)
  }
  // zvýraznění vybraných služeb
  const selectedServiceCards = useMemo(() => {
    const set = new Set()
    items.forEach(i => {
      if (i?.karta) set.add(String(i.karta))
    })
    return set
  }, [items])
  // bloky po výběrech
  const isServiceSelected = (svc) => selectedServiceCards.has(String(svc.karta)) // blok dlaždice
  const isActiveServiceSelected = !!activeService && isServiceSelected(activeService) // blok talčítka
  const isServiceLoading = showServiceModal && !servicesLoaded // loading spinner  
  const availableServiceCategories = useMemo(
    () => new Set(filteredServices.map((service) => service.kat)),
    [filteredServices]
  )
  const categoryServices = useMemo(
    () => filteredServices
      .filter((service) => service.kat === serviceKat)
      .sort((a, b) => String(a.karta ?? '').localeCompare(String(b.karta ?? ''))),
    [filteredServices, serviceKat]
  )
  useEffect(() => {
    if (availableServiceCategories.has(serviceKat)) return
    const firstAvailable = SERVICE_CATS.find((category) => availableServiceCategories.has(category.code))
    if (firstAvailable) setServiceKat(firstAvailable.code)
  }, [availableServiceCategories, serviceKat])
  // mini-form po výběru služby pro zadání počtu a slevy
  const openServiceForm = (svc) => {
    setActiveService(svc)
    setServiceQty('1')
    setServiceDiscount('')
  }
  // vložení položky do objednávky
  const addActiveService = () => {
    if (!activeService) return
    if (isServiceSelected(activeService)) return // nereaguje na opakové přidání stejného řádku
    const hasBonus = !!String(header?.bonus ?? '').trim()
    const kartaNum = Number(String(activeService?.karta ?? '').replace(',', '.')) || 0
    const isBonusService = kartaNum >= 90000 && kartaNum <= 91000
    const sleva = (hasBonus && isBonusService) ? '30' : (serviceDiscount ? String(serviceDiscount) : '')
    const pocet = toNum(serviceQty)
    const cena = Number(activeService.cena ?? 0)
    const dph = Number(activeService.dph ?? 0)
    const slevaFinal = (hasBonus && isBonusService) ? 30 : toNum(serviceDiscount)
    const celkem = round2(pocet * cena * (1 - slevaFinal / 100))
    const slevamj = round2(cena * (toNum(sleva) / 100))
    const celkemsdph = round2(celkem * (1 + dph / 100))
    const cenasdph = round2(cena * (1 + dph / 100))
    const newItem = {
      id: generateId(),
      idrkar: String(activeService.id),
      karta: activeService.karta,
      nazev: activeService.nazev,
      mj: activeService.mj || 'uk',
      typpol: activeService.typpol,
      typradku: '1',
      hmotnost: (Number(activeService.hmotnost ?? 0)).toFixed(4).replace('.', ','),
      pocet: (Number(serviceQty ?? 0)).toFixed(2).replace('.', ','),
      cena: (Number(cena ?? 0)).toFixed(2).replace('.', ','),
      sleva: sleva,
      slevamj: (Number(slevamj ?? 0)).toFixed(2).replace('.', ','),
      dph: String(dph ?? ''),
      cenasdph: cenasdph.toFixed(2),
      celkem: celkem.toFixed(2),
      celkemsdph: celkemsdph.toFixed(2),
      vybavene: '',
      vybavit: '',
      uzvybmj: '0',
    }
    setItems(prev => {
      const isFirstEmpty =
        prev.length === 1 &&
        String(prev[0]?.typradku ?? '1') === '1' &&
        String(prev[0]?.karta ?? '').trim() === '' &&
        String(prev[0]?.nazev ?? '').trim() === ''
      return isFirstEmpty ? [newItem] : [...prev, newItem]
    })
  }
  // vyčištění při zavření modálu 
  const closeServiceModal = () => {
    setShowServiceModal(false)
    setActiveService(null)
    setServiceQty('1')
    setServiceDiscount('')
  }

  /* ------------------------------
      === OPRAVNÝ MATERIÁL ===
  -------------------------------*/
  // definice kategorií
  const MATERIAL_CATS = [
    { code: 'ZÁVAŽÍ', icon: 'bi-database', label: 'závaží',  order: 10 },
    { code: 'VENTILY', icon: 'bi-wind', label: 'ventily', order: 30 }, 
    { code: 'ZÁPLATY', icon: 'bi-bandaid', label: 'záplaty', order: 40 },
    { code: 'VLOŽKY', icon: 'bi-disc', label: 'vložky',  order: 50 },
    { code: 'HŘÍBKY', icon: 'bi-pin', label: 'hříbky',  order: 60 },
    { code: 'OSTATNÍ', icon: 'bi-box', label: 'ostatní', order: 70 },
  ].sort((a, b) => a.order - b.order)
  // načtení skladových karet
  useEffect(() => {
    if (!showMaterialModal || materialLoaded) return
    const load = async () => {
      try {
        const data = await apiFetch('/mrp/material')
        setMaterialAll(Array.isArray(data) ? data : [])
        setMaterialLoaded(true)
      } catch (e) {
        console.error('Načtení materiálu selhalo:', e)
        setMaterialAll([])
      }
    }
    load()
  }, [showMaterialModal, materialLoaded])
  // filtrování
  const filteredMaterial = useMemo(() => {
    return materialAll.filter(m => String(m.kat ?? '').trim() === materialKat)
  }, [materialAll, materialKat])
  // diable pro vybrané
  const selectedMaterialCards = useMemo(() => {
    const set = new Set()
    items.forEach(i => {
      if (i?.karta) set.add(String(i.karta))
    })
    return set
  }, [items])
  const isMaterialSelected = (mat) => selectedMaterialCards.has(String(mat.karta)) // blok dlaždice
  const isActiveMaterialSelected = !!activeMaterial && isMaterialSelected(activeMaterial) // blok tlačítka
  const isMaterialLoading = showMaterialModal && !materialLoaded // loading spinner
  // výběr materiálu a mini form pro vložení počtu a slevy
  const openMaterialForm = (mat) => {
    setActiveMaterial(mat)
    setMaterialQty('1')
    setMaterialDiscount('')
  }
  // vložení položky do objednávky
  const addActiveMaterial = () => {
    if (!activeMaterial) return
    if (isMaterialSelected(activeMaterial)) return // nereaguje na opakové přidání stejného řádku
    const karta = activeMaterial.karta
    const cena = Number(activeMaterial.cena ?? 0)
    const dph = Number(activeMaterial.dph ?? 0)
    const sleva = toNum(materialDiscount)
    const slevamj = round2(cena * (sleva / 100))
    setItems(prev => {
      const isFirstEmpty =
        prev.length === 1 &&
        String(prev[0]?.typradku ?? '1') === '1' &&
        String(prev[0]?.karta ?? '').trim() === '' &&
        String(prev[0]?.nazev ?? '').trim() === ''
      // pokud je první prázdný, přepíšeme ho rovnou
      if (isFirstEmpty) {
        const newItem = {
          id: generateId(),
          idrkar: String(activeMaterial.id),
          karta,
          nazev: activeMaterial.nazev,
          mj: activeMaterial.mj || 'ks',
          typpol: activeMaterial.typpol,
          typradku: '1',
          hmotnost: (Number(activeMaterial.hmotnost ?? 0)).toFixed(4).replace('.', ','),
          pocet: (Number(materialQty ?? 0)).toFixed(2).replace('.', ','),
          cena: Number(cena).toFixed(2).replace('.', ','),
          sleva: materialDiscount ? String(materialDiscount) : '',
          slevamj: slevamj.toFixed(2),
          dph: String(dph ?? ''),
          cenasdph: '',
          celkem: '',
          celkemsdph: '',
          vybavene: '',
          vybavit: '',
          uzvybmj: '0',
        }
        return [recalcRow(newItem)]
      }      
      // jinak nový řádek
      const newItem = recalcRow({
        id: generateId(),
        idrkar: String(activeMaterial.id),
        karta,
        nazev: activeMaterial.nazev,
        mj: activeMaterial.mj || 'ks',
        typpol: activeMaterial.typpol,
        typradku: '1',
        hmotnost: (Number(activeMaterial.hmotnost ?? 0)).toFixed(4).replace('.', ','),
        pocet: (Number(materialQty ?? 0)).toFixed(2).replace('.', ','),
        cena: Number(cena).toFixed(2).replace('.', ','),
        sleva: materialDiscount ? String(materialDiscount) : '',
        slevamj: slevamj.toFixed(2),
        dph: String(dph ?? ''),
        cenasdph: '',
        celkem: '',
        celkemsdph: '',
        vybavene: '',
        vybavit: '',
        uzvybmj: '0',
      })
      return [...prev, newItem]
    })
  }
  // vyčištění modálu při zavření
  const closeMaterialModal = () => {
    setShowMaterialModal(false)
    setActiveMaterial(null)
    setMaterialQty('1')
    setMaterialDiscount('')
  }  

  /* ------------------------------
      === POLOŽKY DOKLADU ===
  -------------------------------*/
  // Přidání řádku dokladu
  const addRow = () => {
    const newItem = { id: generateId(), idrkar: '', karta: '', nazev: '', mj: '', typpol: '', typradku: '1', hmotnost: '', pocet: '', cena: '', dph: '', sleva: '', slevamj: '', cenasdph: '', celkem: '', celkemsdph: '', vybavene: '', vybavit: '', uzvybmj: '0' }
    setItems((items) => [...items, newItem])
  }
  const addTextRow = () => {
    const newItem = { id: generateId(), idrkar: '', karta: '', nazev: '', mj: '', typpol: '', typradku: '2', hmotnost: '', pocet: '', cena: '', dph: '', sleva: '', slevamj: '', cenasdph: '', celkem: '', celkemsdph: '', vybavene: '', vybavit: '', uzvybmj: '' }
    setItems((prev) => {
      const isFirstEmpty =
        prev.length === 1 &&
        String(prev[0]?.typradku ?? '1') === '1' &&
        String(prev[0]?.karta ?? '').trim() === '' &&
        String(prev[0]?.nazev ?? '').trim() === ''
      return isFirstEmpty ? [newItem] : [...prev, newItem]
    })
  }
  // Odstranění řádku dokladu
  const removeRow = (index) => {
    const updatedItems = [...items]
    updatedItems.splice(index, 1)
    setItems(updatedItems)
  }

  const removeSelectedCard = (card) => {
    const normalizedCard = String(card ?? '').trim()
    if (!normalizedCard) return
    setItems((prev) => prev.filter((item) => String(item?.karta ?? '').trim() !== normalizedCard))
    if (String(activeService?.karta ?? '').trim() === normalizedCard) setActiveService(null)
    if (String(activeMaterial?.karta ?? '').trim() === normalizedCard) setActiveMaterial(null)
  }
  // Přesun pořadí řádků
  const handleDragEnd = (result) => {
    if (!result.destination) return
    const updatedItems = Array.from(items)
    const [movedItem] = updatedItems.splice(result.source.index, 1)
    updatedItems.splice(result.destination.index, 0, movedItem)
    setItems(updatedItems)
  }
  // přepočty na řádku při ruční změně
  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev]
      const item = { ...updated[index], [field]: value }
      if (String(item.typradku ?? '1') === '2') {
        updated[index] = item
        return updated
      }
      updated[index] = recalcRow(item)
      return updated
    })
  } 
  

  /* ------------------------------
      === OSTATNÍ POMOCNÉ ===
  -------------------------------*/
  // Zavření datalistů 
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowList(false)
      }
      if (wrapperKPRef.current && !wrapperKPRef.current.contains(event.target)) {
        setShowListKP(false)
      }
      if (wrapperBKRef.current && !wrapperBKRef.current.contains(event.target)) {
        setShowListBonus(false)
      }
      if (wrapperZKRef.current && !wrapperZKRef.current.contains(event.target)) {
        setShowListZakazky(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setShowList,setShowListKP,setShowListBonus,setShowListZakazky])  

  // Zavření modálů
  const closeModal = () => {
    setShowAddAddressModal(false)
    setShowEditAddressModal(false)
    setShowAddAddressModalKP(false)
    setShowEditAddressModalKP(false)
    setValidatedAddress(false)
    setNewAddress({})
    setShowBonusModal(false)
    setValidatedBonus(false)
    setNewBonus({})
    setIsError(false)
    setIcoInput('')
    setErrorMsg('')
    setDphStatus('')
  }
  // Otevření modálů
  const openAddModalAddress = () => setShowAddAddressModal(true)  
  const openEditModalAddress = () => setShowEditAddressModal(true)  
  const openAddModalAddressKP = () => setShowAddAddressModalKP(true)  
  const openEditModalAddressKP = () => setShowEditAddressModalKP(true)
  const openModalBonus = () => setShowBonusModal(true)  

  // Formátování měny do celkových částek
  const formatCZK = (value) => {
    if (value === '' || value === null || value === undefined || isNaN(value) || value === 0 || value === '0.00') return ''
    return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK' }).format(parseFloat(value))
  }
  const formatDateCZ = (value) => {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('cs-CZ')
  }

  /* ------------------------------
      === ULOŽENÍ OBJEDNÁVKY ===
  -------------------------------*/
  const submitOrder = async (form, stav = '') => {
    const hasValidItems = Array.isArray(items) && items.some((item) => {
      const karta = String(item?.karta ?? '').trim()
      const nazev = String(item?.nazev ?? '').trim()
      const idrkar = String(item?.idrkar ?? '').trim()
      const pocet = toNum(item?.pocet)
      const cena = toNum(item?.cena)
      return Boolean(karta || nazev || idrkar || pocet !== 0 || cena !== 0)
    })
    if (!hasValidItems) {
      const newNotification = {
        id: Date.now(),
        type: 'error',
        message: 'Nelze uložit prázdnou objednávku.',
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])
      return
    }
    const isValid = form.checkValidity()
    setValidatedOrder(true)
    if (!isValid) {
      const newNotification = {
        id: Date.now(),
        type: 'error',
        message: `Neúplné nebo chybné vstupní údaje. Doplňte nebo opravte.`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])
      return
    }
    const vybaveneValues = (items || [])
      .filter((item) => String(item?.typradku ?? '1') === '1')
      .map((item) => String(item?.vybavene ?? '').trim())
    const nextVybavene =
      vybaveneValues.length > 0 && vybaveneValues.every((v) => v === '0')
        ? '0'
        : vybaveneValues.length > 0 && vybaveneValues.every((v) => v === '2')
        ? '2'
        : '1'
    const selectedStredisko = strediska?.[0]
    const dataToSubmit = {
      ...header,
      cislostr: String(selectedStredisko?.cislo ?? header.cislostr ?? ''),
      stav: String(stav || header.stav || 'prijato'),
      vybavene: nextVybavene,
      polozky: items
    }
    try {
        await apiFetch('/mrp/zmenaobjednavka', {
        method: 'PUT',
        body: JSON.stringify(dataToSubmit),
      })
      navigate('/orders', { state: { type: 'update' } })
    } catch (error) {
      console.error('Chyba při ukládání záznamu:', error)
      const newNotification = {
        id: Date.now(),
        type: 'error',
        message: `Chyba při ukládání záznamu: ${error.message}`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    await submitOrder(e.currentTarget, header.stav || 'prijato')
  }

  const handleSubmitUzavreno = async () => {
    if (!orderFormRef.current) return
    await submitOrder(orderFormRef.current, 'provedeno')
  }

  const handleSubmitDilna = async () => {
    if (!orderFormRef.current) return
    await submitOrder(orderFormRef.current, 'dilna')
  }

  const isStateChangeLocked = ['0', '1'].includes(String(header?.vybavene ?? '').trim())
  const showSkladovyPohybBadge =
    String(header?.vybavene ?? '').trim() === '0' &&
    String(header?.skladovypohyb || '').trim() !== ''

  return (
    <>
    <Form
      ref={orderFormRef}
      noValidate
      validated={validatedOrder}
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
        }
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Hlavička</h5>
        <div className="d-flex align-items-center gap-1">
          <div className="fw-semibold px-3 py-1 rounded-3 bg-primary-subtle text-primary">
            Objednávka: {header.cislo || '-'}
          </div>
           {showSkladovyPohybBadge && (
            <div className="fw-semibold px-3 py-1 rounded-3 bg-secondary-subtle text-secondary">
              Pohyb: {`V${header.skladovypohyb}`}
            </div>
          )}
          {orderStatus?.label && (
            <div
              className={`fw-semibold px-3 py-1 rounded-3 ${orderStatus?.color || ''}`}
              style={{ backgroundColor: orderStatus?.bg || 'rgba(var(--bs-secondary-rgb), 0.08)' }}
              title={orderStatus.label}
            >
              {orderStatus?.icon && <i className={`bi ${orderStatus.icon} me-2`}></i>}
              {orderStatus.label}
            </div>
          )}         
        </div>
      </div>
      <Row className='d-flex justify-content-between g-2 align-items-end'>        
        {/* Tabs */}
        <Col sm={12} md={5} xl={4}>
          <Tabs
            defaultActiveKey="firma"
            id="tab-firma"
            className="mb-3"
          >
            {/* Hlavní Adresa */}    
            <Tab eventKey="firma" title="Firma">                      
              <Row className='d-flex g-1 mb-3 align-items-center'> 
                <Col sm={12}>
                  <Row className='g-1'>
                    <Col className="flex-grow-1">
                      <Form.Group controlId="formFirma" className="position-relative" ref={wrapperRef}>
                        <FloatingLabel label="firma">
                          <OrderFieldClearButton show={Boolean(search)} withInfo onClear={clearCompany} label="Vymazat firmu" />
                           <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-adresa">
                                vyhledej zákazníka v adresáři podle názvu nebo IČ, vlož alespoň 3 znaky
                              </Tooltip>
                            }
                          >
                            <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                          </OverlayTrigger> 
                          <Form.Control
                            className="order-field-with-clear"
                            value={search}
                            onChange={handleChange}
                            required
                            autoComplete="off"
                            onFocus={() => setShowList(true)}
                          />                          
                        </FloatingLabel>
                        {showList && (
                          <ul
                            className="list-group position-absolute w-100"
                            style={{ zIndex: 1000, maxHeight: '225px', overflowY: 'auto' }}
                          >
                            {firmy.length > 0 ? (
                              firmy.map((firma) => (
                                <li
                                  key={firma.IDRADR}
                                  className="list-group-item list-group-item-action d-flex"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleSelect(firma)}
                                >
                                  <span style={{ minWidth: '100px' }}>{firma.ICO}</span>
                                  <span>{firma.FIRMA}, {firma.MESTO}</span>
                                </li>
                              ))
                            ) : search.trim() !== '' && (
                              <li className="list-group-item text-muted">žádný záznam nenalezen</li>
                            )}
                          </ul>
                        )}                
                      </Form.Group>              
                    </Col> 
                    {/* Tlačítka zarovnaná doprava */}
                    <Col xs="auto" className="d-flex gap-1">
                      <Button 
                        variant="primary" 
                        title='Přidat adresu'
                        onClick={openAddModalAddress}
                      >
                        <i className="bi bi-plus-circle"></i>
                      </Button>
                      <Button 
                        variant="primary" 
                        title='Upravit adresu'
                        disabled={!header?.idradr}
                        onClick={openEditModalAddress}
                      >
                        <i className="bi bi-pencil-square"></i>
                      </Button>
                    </Col>  
                  </Row> 
                </Col>         
                <Col sm={12}>
                  <Form.Group controlId="formUllice">
                    <FloatingLabel label="ulice">
                    <Form.Control
                      type="text"
                      name="ulice"
                      value={header.ulice}
                      readOnly
                      disabled
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>  
                <Col sm={12}>
                  <Row className='g-1'>
                    <Col sm={4}>
                      <Form.Group controlId="formPsc">
                        <FloatingLabel label="psč">
                        <Form.Control
                          type="text"
                          name="psc"
                          value={header.psc}
                          readOnly
                          disabled
                        />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col sm={8}>
                      <Form.Group controlId="formMesto">
                        <FloatingLabel label="město">
                        <Form.Control
                          type="text"
                          name="mesto"
                          value={header.mesto}
                          readOnly
                          disabled
                        />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>              
                </Col>
                <Col sm={12}>
                  <Form.Group controlId="formIc">
                    <FloatingLabel label="ič">
                    <Form.Control
                      type="text"
                      name="ico"
                      value={header.ico}
                      readOnly
                      disabled
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>                                  
                <Col sm={12}>
                  <Form.Group controlId="formDic">
                    <FloatingLabel label="dič">
                    <Form.Control
                      type="text"
                      name="dic"
                      value={header.dic}
                      readOnly
                      disabled
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>                   
              </Row>        
            </Tab>
            {/* Konečný příjemce */}
            <Tab eventKey="konprij" title="Kon.příjemce">
              {/* Konečný příjemce */}
              <Row className='d-flex g-1 mb-3 align-items-center'> 
                <Col sm={12}>
                  <Row className='g-1'>
                    <Col className="flex-grow-1">
                      <Form.Group controlId="formKPFirma" className="position-relative" ref={wrapperKPRef}>
                        <FloatingLabel label="firma">
                          <OrderFieldClearButton show={Boolean(kpsearch)} withInfo onClear={clearFinalRecipient} label="Vymazat konečného příjemce" />
                           <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-kpadresa">
                                vyhledej zákazníka v adresáři podle názvu nebo IČ, vlož alespoň 3 znaky
                              </Tooltip>
                            }
                          >
                            <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                          </OverlayTrigger> 
                          <Form.Control
                            className="order-field-with-clear"
                            value={kpsearch}
                            onChange={handleChangeKP}
                            autoComplete="off"
                            onFocus={() => setShowListKP(true)}
                          />                          
                        </FloatingLabel>
                        {showListKP && (
                          <ul
                            className="list-group position-absolute w-100"
                            style={{ zIndex: 1000, maxHeight: '225px', overflowY: 'auto' }}
                          >
                            {kpfirmy.length > 0 ? (
                              kpfirmy.map((kpfirma) => (
                                <li
                                  key={kpfirma.IDRADR}
                                  className="list-group-item list-group-item-action d-flex"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleSelectKP(kpfirma)}
                                >
                                  <span style={{ minWidth: '100px' }}>{kpfirma.ICO}</span>
                                  <span>{kpfirma.FIRMA}, {kpfirma.MESTO}</span>
                                </li>
                              ))
                            ) : kpsearch.trim() !== '' && (
                              <li className="list-group-item text-muted">žádný záznam nenalezen</li>
                            )}
                          </ul>
                        )}                
                      </Form.Group>              
                    </Col> 
                    {/* Tlačítka zarovnaná doprava */}
                    <Col xs="auto" className="d-flex gap-1">
                      <Button 
                        variant="primary" 
                        title='Přidat adresu'
                        onClick={openAddModalAddressKP}
                      >
                        <i className="bi bi-plus-circle"></i>
                      </Button>
                      <Button 
                        variant="primary" 
                        title='Upravit adresu'
                        disabled={!header?.kpidradr}
                        onClick={openEditModalAddressKP}
                      >
                        <i className="bi bi-pencil-square"></i>
                      </Button>
                    </Col>  
                  </Row> 
                </Col>         
                <Col sm={12}>
                  <Form.Group controlId="formKPUllice">
                    <FloatingLabel label="ulice">
                    <Form.Control
                      type="text"
                      name="kpulice"
                      value={header.kpulice}
                      readOnly
                      disabled
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>  
                <Col sm={12}>
                  <Row className='g-1'>
                    <Col sm={4}>
                      <Form.Group controlId="formKPPsc">
                        <FloatingLabel label="psč">
                        <Form.Control
                          type="text"
                          name="kppsc"
                          value={header.kppsc}
                          readOnly
                          disabled
                        />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col sm={8}>
                      <Form.Group controlId="formKPMesto">
                        <FloatingLabel label="město">
                        <Form.Control
                          type="text"
                          name="kpmesto"
                          value={header.kpmesto}
                          readOnly
                          disabled
                        />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>              
                </Col>
                <Col sm={12}>
                  <Form.Group controlId="formKPIc">
                    <FloatingLabel label="ič">
                    <Form.Control
                      type="text"
                      name="icoprij"
                      value={header.icoprij}
                      readOnly
                      disabled
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>                                  
                <Col sm={12}>
                  <Form.Group controlId="formKPDic">
                    <FloatingLabel label="dič">
                    <Form.Control
                      type="text"
                      name="kpdic"
                      value={header.kpdic}
                      readOnly
                      disabled
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>                   
              </Row>              
            </Tab> 
            {/* Bonus kaarta */}
            <Tab eventKey="bonus" title="Bonus karta">
              {/* Bonus karta */}            
              <Row className='d-flex g-1 mb-3 align-items-center'> 
                <Col sm={12}>
                  <Row className='g-1'>
                    <Col className="flex-grow-1">
                      <Form.Group controlId="formBKCislo" className="position-relative" ref={wrapperBKRef}>
                        <FloatingLabel label="číslo">
                          <OrderFieldClearButton show={Boolean(searchBonus)} withInfo onClear={clearBonusCard} label="Vymazat bonus kartu" />
                           <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-bonus">
                                vyhledej bonus kartu podle čísla karty, jména, emailu nebo telefonu, vlož alespoň 3 znaky
                              </Tooltip>
                            }
                          >
                            <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                          </OverlayTrigger> 
                          <Form.Control
                            className="order-field-with-clear"
                            value={searchBonus}
                            onChange={handleChangeBonus}
                            pattern="^(900|990)\d{4}$"
                            title="Číslo bonus karty musí mít 7 číslic a začínat na 900 nebo 990"
                            autoComplete="off"
                            onFocus={() => setShowListBonus(true)}
                          />                          
                        </FloatingLabel>
                        {showListBonus && (
                          <ul
                            className="list-group position-absolute w-100"
                            style={{ zIndex: 1000, maxHeight: '225px', overflowY: 'auto' }}
                          >
                            {bonusKarty.length > 0 ? (
                              bonusKarty.map((karta) => (
                                <li
                                  key={karta.IDR}
                                  className="list-group-item list-group-item-action d-flex"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleSelectBonus(karta)}
                                >
                                  <span style={{ minWidth: '100px' }}>{karta.CISLO}</span>
                                  <span>{karta.JMENO}, {karta.TELEFON}</span>
                                </li>
                              ))
                            ) : searchBonus.trim() !== '' && (
                              <li className="list-group-item text-muted">žádný záznam nenalezen</li>
                            )}
                          </ul>
                        )}                
                      </Form.Group>              
                    </Col> 
                    {/* Tlačítka zarovnaná doprava */}
                    <Col xs="auto" className="d-flex gap-1">
                      <Button
                        variant="primary"
                        title="Přidat adresu"
                        onClick={() => {
                          setMode('add')              
                          openModalBonus()            
                        }}
                      >
                        <i className="bi bi-plus-circle"></i>
                      </Button>
                      <Button
                        variant="primary"
                        title="Upravit adresu"
                        disabled={!header?.idrbonus}
                        onClick={() => {
                          setMode('edit')
                          openModalBonus()
                        }}
                      >
                        <i className="bi bi-pencil-square"></i>
                      </Button>
                    </Col>
                  </Row> 
                </Col>         
                <Col sm={12}>
                  <Form.Group controlId="formBKJmeno">
                    <FloatingLabel label="jméno">
                    <Form.Control
                      type="text"
                      name="BKjmeno"
                      value={header?.bonusjmeno || ''}
                      readOnly
                      disabled
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col> 
                <Col sm={12}>
                  <Form.Group controlId="formBKTelefon">
                    <FloatingLabel label="telefon">
                    <Form.Control
                      type="text"
                      name="BKtelefon"
                      value={header?.bonustelefon || ''}
                      readOnly
                      disabled
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>  
                <Col sm={12}>
                  <Form.Group controlId="formBKEmail">
                    <FloatingLabel label="email">
                    <Form.Control
                      type="text"
                      name="BKemail"
                      value={header?.bonusemail || ''}
                      readOnly
                      disabled
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>                                                  
                <Col sm={12}>
                  <Form.Group controlId="formBKAdresa">
                    <FloatingLabel label="adresa">
                    <Form.Control
                      type="text"
                      name="BKadresa"
                      value={header?.bonusadresa || ''}
                      readOnly
                      disabled
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>                   
              </Row> 
            </Tab>
          </Tabs>
        </Col>               
        {/* Hlavička ostatní parametry */}
        <Col sm={12} md={5} xl={4}>
          <Row className="g-1 mb-3 ms-md-3 ms-xxl-5">
            <Col xs={12} md={6}>
              <Form.Group controlId="formDatum">
                <FloatingLabel label="datum">
                <Form.Control
                  type="date"
                  name="datum"
                  value={header.datum || formatDateForInput(new Date())}
                  onChange={handleHeaderChange}
                  required
                />
                </FloatingLabel>
              </Form.Group>
            </Col>            
            <Col xs={12} md={6}>
              <Row className="g-1">
                <Col xs={8}>
                  <Form.Group controlId="formStredisko">
                    <FloatingLabel label={'středisko'}>
                      <Form.Control
                        type="text"
                        name="stredisko"
                        value={(() => {
                          const selected = strediska[0]
                          if (!selected) return ''
                          const cislo = String(selected.cislo ?? '').trim()
                          const nazev = String(selected.nazev ?? '').replace(/pneuservis/gi, '').trim()
                          return `${cislo} ${nazev}`.trim()
                        })()}
                        readOnly
                        disabled
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={4}>
                  <Form.Group controlId="formStani">
                    <FloatingLabel label="č.stání">
                      <Form.Select
                        name="stani"
                        value={header?.stani || ''}
                        onChange={handleHeaderChange}
                        required
                      >
                        {(() => {
                          const selected = strediska[0]
                          const count = Number(selected?.stani) || 0
                          return Array.from({ length: count }, (_, i) => {
                            const num = i + 1
                            return (
                              <option key={`stani-${num}`} value={String(num)}>
                                {num}
                              </option>
                            )
                          })
                        })()}
                      </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="formUhrada">
                <FloatingLabel label="způsob úhrady">
                  <Form.Select
                    name="uhrada"
                    value={header.uhrada}
                    onChange={handleHeaderChange}
                    required
                  >
                    <option value="hotovost">hotově</option>
                    <option value="faktura">na fakturu</option>
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="formBonusBody">
                <FloatingLabel label="stav bonus karty">
                <Form.Control
                    type="text"
                    name="stavbonus"
                    value={header.stavbonus !== undefined && header.stavbonus !== '' 
                      ? header.stavbonus + ' bodů' 
                      : ''
                    }
                    disabled                    
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="formZakazka" className="position-relative" ref={wrapperZKRef}>
                <FloatingLabel label="zakázka">
                  <OrderFieldClearButton show={Boolean(searchZakazka)} withInfo onClear={clearJob} label="Vymazat zakázku" />
                  <OverlayTrigger
                    placement="top"                    
                    overlay={
                      <Tooltip id="tooltip-zakazka">
                        vyhledej zakázku, vlož alespoň 3 znaky
                      </Tooltip>
                    }
                  >
                    <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                  </OverlayTrigger> 
                  <Form.Control
                    className="order-field-with-clear"
                    value={searchZakazka}
                    onChange={handleZakazka}                    
                    autoComplete="off"
                    onFocus={() => setShowListZakazky(true)}
                  />
                </FloatingLabel>
                {showListZakazky && (
                  <ul
                    className="list-group position-absolute"
                    style={{
                      zIndex: 1000,
                      maxHeight: '225px',
                      overflowY: 'auto',
                      minWidth: '350px',   // minimální šířka seznamu
                      left: '0',           // odleva od parentu
                      top: '100%',         // pod input
                      backgroundColor: 'white',
                      boxShadow: '0 0.25rem 0.5rem rgba(0,0,0,0.15)',
                    }}
                  >
                    {zakazky.length > 0 ? (
                      zakazky.map((zakazka) => (
                        <li
                          key={zakazka.IDR}
                          className="list-group-item list-group-item-action d-flex flex-column"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSelectZakazka(zakazka)}
                        >
                          <strong>{zakazka.CISLO || '-'}</strong>
                          <span className="text-muted small">{[zakazka.FIRMA, zakazka.ICO].filter(Boolean).join(', ') || '-'}</span>
                          <span className="text-muted small">{[zakazka.NAZOV, zakazka.POZNAMKA, zakazka.POZNAMKA2].filter(Boolean).join(', ') || '-'}</span>
                        </li>
                      ))
                    ) : searchZakazka.trim() !== '' && (
                      <li className="list-group-item text-muted">žádný záznam nenalezen</li>
                    )}
                  </ul>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="formNP">
                <FloatingLabel label="zůstatek v náhradním plnění">
                  <Form.Control
                    type="text"
                    name="np"
                    value={
                      header.nplimit !== undefined && header.nplimit !== ''
                        ? new Intl.NumberFormat('cs-CZ', {
                            style: 'currency',
                            currency: 'CZK'
                          }).format(header.nplimit - header.npfakturace)
                        : ''
                    }
                    disabled
                    style={{
                      color:
                        header.nplimit - header.npfakturace > 0
                          ? 'green'
                          : header.nplimit - header.npfakturace < 0
                          ? 'red'
                          : 'inherit'
                    }}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>        
            <Col xs={12} md={6}>
              <Form.Group controlId="formOrigcislo" className="position-relative">
                <FloatingLabel label="pův. číslo obj.">
                <OrderFieldClearButton show={Boolean(header.origcislo)} onClear={() => setHeader((prev) => ({ ...prev, origcislo: '' }))} label="Vymazat původní číslo objednávky" />
                <Form.Control
                  className="order-field-with-clear-only"
                  type="text"
                  name="origcislo"
                  value={header.origcislo}
                  onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                  onChange={handleHeaderChange}
                  maxLength={32}
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <div className="w-100 d-none d-md-block m-0"></div>
            <Col xs={12} md={6}>
              <Form.Group controlId="formSpz" className="position-relative">
                <FloatingLabel label="spz">
                <OrderFieldClearButton show={Boolean(header.spz)} onClear={() => setHeader((prev) => ({ ...prev, spz: '' }))} label="Vymazat SPZ" />
                <Form.Control
                  className="order-field-with-clear-only"
                  type="text"
                  name="spz"
                  value={header.spz}                  
                  onChange={handleHeaderChange}
                  onBlur={(e) =>
                    setHeader((prev) => ({
                      ...prev,
                      spz: String(e.target.value || '').trim().replace(/\s+/g, '').toUpperCase()
                    }))
                  }
                  maxLength={32}
                />
                </FloatingLabel>
              </Form.Group>
            </Col>            
          </Row>
        </Col>  
        {/* Celkové částky */}
        <Col sm={12} md={2} xl={4}>
          <Row className='g-1 mb-3 ms-md-3 ms-xxl-5 d-flex flex-column justify-content-end align-items-end'>
            <Col sm={12} xl={6}>
              <Form.Group controlId="formCelkem">
                <FloatingLabel label="celkem">
                  <Form.Control
                    type="text"
                    name="celkem"
                    className='fw-bold'
                    value={formatCZK(header.celkem)}
                    disabled
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col sm={12} xl={6}>
              <Form.Group controlId="formZaklad">
                <FloatingLabel label="základ">
                  <Form.Control
                    type="text"
                    name="zaklad"
                    className=''
                    value={formatCZK(header.zaklad)}
                    disabled
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col sm={12} xl={6}>
              <Form.Group controlId="formDph">
                <FloatingLabel label="dph">
                  <Form.Control
                    type="text"
                    name="dph"
                    className=''
                    value={formatCZK(header.dph)}
                    disabled
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12} xl={6} className='mt-2'>
              <Form.Group controlId="formSleva">
                <FloatingLabel label="celkem sleva (s dph)">
                <Form.Control
                  type="text"
                  name="celkemsleva"
                  value={formatCZK(header.celkemsleva)}
                  disabled
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col sm={12} xl={6}>
              <Form.Group controlId="formKredit">
                <FloatingLabel label="kredit">
                <Form.Control
                  type="text"
                  name="kredit"
                  value={formatCZK(header.kredit)}
                  readOnly
                  disabled
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
        </Col>                   
      </Row>     
      {/* Položky */}
      <Row className='mb-3 g-1'>
        <h5 className='mt-3 mb-0'>Položky</h5>
        <Col sm={12}>
          <div className="table table-responsive mb-1 order-items-scroll">
            <div className="table-header d-flex fw-bold small">
              <div style={{ width: '2%', minWidth: '15px', padding: '0.5rem' }}></div>
              <div style={{ width: '9%', minWidth: '100px', padding: '0.5rem' }}>karta</div>
              <div style={{ width: '33%', minWidth: '200px', padding: '0.5rem' }}>název</div>              
              <div style={{ width: '5%', minWidth: '50px', padding: '0.5rem' }}>mj</div>
              <div style={{ width: '8%', minWidth: '80px', padding: '0.5rem' }}>počet</div>
              <div style={{ width: '8%', minWidth: '100px', padding: '0.5rem' }}>cena</div>
              <div style={{ width: '6%', minWidth: '50px', padding: '0.5rem' }}>sleva</div>
              <div style={{ width: '6%', minWidth: '50px', padding: '0.5rem' }}>dph</div>
              {/* <div style={{ width: '12%', minWidth: '125px', padding: '0.5rem' }}>cena (s dph)</div> */}
              <div style={{ width: '10%', minWidth: '125px', padding: '0.5rem' }}>celkem</div>
              <div style={{ width: '10%', minWidth: '125px', padding: '0.5rem' }}>celkem (s dph)</div>
              <div style={{ width: '3%', minWidth: '50px', padding: '0.5rem' }}>akce</div>
            </div>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="items">
                {(provided) => (
                  <div
                    className="table-body mb-2 dragdrop-table-body2"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {items.map((item, index) => {
                      const isTextRow = String(item?.typradku ?? '1') === '2'
                      if (isTextRow) {
                        return (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(provided) => (
                              <div
                                className="table-row d-flex align-items-center dragdrop-table-row2"
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <div className="text-start" style={{ width: '2%', minWidth: '15px' }}>
                                  <i className="bi bi-grip-vertical" style={{ fontSize: '1.25rem', cursor: 'grab' }}></i>
                                </div>
                                <div className="p-1" style={{ width: '95%', minWidth: '300px' }}>
                                  <Form.Control
                                    name="nazev"
                                    type="text"
                                    className="p-3"
                                    value={item.nazev}
                                    placeholder="textový řádek"
                                    onChange={(e) => handleItemChange(index, 'nazev', e.target.value)}
                                    maxLength={100}
                                    autoComplete="off"
                                  />
                                </div>
                                <div className="p-1 text-center" style={{ width: '3%', minWidth: '50px' }}>
                                  <Button
                                    className="btn-table"
                                    variant="outline-light"
                                    title="Odstranit řádek"
                                    onClick={() => removeRow(index)}
                                    disabled={items.length === 1}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        )
                      }
                      return (
                        <Draggable key={item.id} draggableId={item.id} index={index}>
                          {(provided) => (
                            <div
                              className="table-row d-flex align-items-center dragdrop-table-row2"    
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              {/* grip pro přetahování řádků */}
                              <div
                                className="text-start"
                                style={{ width: '2%', minWidth: '15px' }}
                              >
                                <i
                                  className="bi bi-grip-vertical"
                                  style={{ fontSize: '1.25rem', cursor: 'grab' }}
                                ></i>
                              </div>
                              {/* karta */}
                              <div className="p-1" style={{ width: '9%', minWidth: '100px' }}>
                                <Form.Control
                                  name="karta"
                                  type="text"
                                  className="p-3"
                                  disabled
                                  value={String(item.karta ?? '')}
                                  onChange={(e) => {
                                    let v = e.target.value
                                    // povol jen čísla + čárka/tečka
                                    v = v.replace(/[^0-9.,]/g, '')
                                    // sjednoť tečku na čárku (uživatelské)
                                    v = v.replace(/\./g, ',')
                                    // povol jen jednu čárku
                                    const parts = v.split(',')
                                    if (parts.length > 2) {
                                      v = parts[0] + ',' + parts.slice(1).join('') // sloučí zbytek
                                    }
                                    // max 2 desetinná místa
                                    const [intPart, decPart] = v.split(',')
                                    if (decPart !== undefined) {
                                      v = intPart + ',' + decPart.slice(0, 2)
                                    }
                                    handleItemChange(index, 'karta', v)
                                  }}
                                  onBlur={() => {
                                    const v = String(item.karta ?? '').trim()
                                    if (!v) return
                                    // 123 -> 123,00 | 123,2 -> 123,20 | 123,25 -> 123,25
                                    const [intPart, decPart = ''] = v.split(',')
                                    if (!intPart) return
                                    const formatted =
                                      decPart.length === 0 ? `${intPart},00`
                                      : decPart.length === 1 ? `${intPart},${decPart}0`
                                      : `${intPart},${decPart.slice(0, 2)}`
                                    // nastav jen když se liší (méně rerenderu)
                                    if (formatted !== v) handleItemChange(index, 'karta', formatted)
                                  }}
                                  pattern="^\d+(,\d{0,2})?$"
                                  //title="Zadej číslo karty, max. dvě desetinná místa (např. 26025,01)"
                                  autoComplete="off"
                                />
                              </div>
                              {/* název */}
                              <div className={`p-1 position-relative`} style={{ width: '33%', minWidth: '200px' }}>
                                <Form.Control
                                  name="nazev"
                                  type="text"
                                  disabled
                                  value={item.nazev}
                                  className="p-3"
                                  onChange={(e) => handleItemChange(index, 'nazev', e.target.value)}
                                  required
                                  maxLength={128}
                                  //title="povinné pole"
                                  autoComplete="off"
                                />                               
                              </div>
                              {/* mj */}
                              <div className="p-1" style={{ width: '5%', minWidth: '50px' }}>
                                <Form.Control
                                  name="mj"
                                  className="p-3"
                                  value={item.mj}
                                  disabled
                                  required
                                />                                  
                              </div>
                              {/* počet */}
                              <div className="p-1" style={{ width: '8%', minWidth: '80px' }}>
                                <Form.Control
                                  name="pocet"
                                  type="text"
                                  className="p-3"
                                  required
                                  value={String(item.pocet ?? '')}
                                  onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                                  onChange={(e) => {
                                    let v = e.target.value
                                    v = v.replace(/[^0-9.,]/g, '')
                                    v = v.replace(/\./g, ',')
                                    const parts = v.split(',')
                                    if (parts.length > 2) v = parts[0] + ',' + parts.slice(1).join('')
                                    const [i, d] = v.split(',')
                                    if (d !== undefined) v = i + ',' + d.slice(0, 2)  // max 2 desetiny v UI
                                    handleItemChange(index, 'pocet', v)
                                  }}
                                  onBlur={() => {
                                    const v = String(item.pocet ?? '').trim()
                                    if (!v) return
                                    const [i, d = ''] = v.split(',')
                                    if (!i) return
                                    const formatted =
                                      d.length === 0 ? `${i},00`
                                      : d.length === 1 ? `${i},${d}0`
                                      : `${i},${d.slice(0, 2)}`
                                    if (formatted !== v) handleItemChange(index, 'pocet', formatted)
                                  }}
                                  pattern="^\d+(,\d{0,2})?$"
                                  title="Zadej počet, max. dvě desetinná místa"
                                  autoComplete="off"
                                />
                              </div>
                              {/* cena */}
                              <div className="p-1" style={{ width: '8%', minWidth: '100px' }}>
                                <Form.Control
                                  name="cena"
                                  type="text"
                                  className="p-3"
                                  required
                                  value={String(item.cena ?? '')}
                                  onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                                  onChange={(e) => {
                                    let v = e.target.value
                                    v = v.replace(/[^0-9.,]/g, '')
                                    v = v.replace(/\./g, ',')
                                    const parts = v.split(',')
                                    if (parts.length > 2) v = parts[0] + ',' + parts.slice(1).join('')
                                    const [i, d] = v.split(',')
                                    if (d !== undefined) v = i + ',' + d.slice(0, 2)
                                    handleItemChange(index, 'cena', v)
                                  }}
                                  onBlur={() => {
                                    const v = String(item.cena ?? '').trim()
                                    if (!v) return
                                    const [i, d = ''] = v.split(',')
                                    if (!i) return
                                    const formatted =
                                      d.length === 0 ? `${i},00`
                                      : d.length === 1 ? `${i},${d}0`
                                      : `${i},${d.slice(0, 2)}`
                                    if (formatted !== v) handleItemChange(index, 'cena', formatted)
                                  }}
                                  pattern="^\d+(,\d{0,2})?$"
                                  title="Zadej cenu, max. dvě desetinná místa"
                                  autoComplete="off"
                                />
                              </div>
                              {/* sleva */}
                              <div className="p-1" style={{ width: '6%', minWidth: '100px' }}>
                                <Form.Control
                                  name="sleva"
                                  type="text"
                                  className="p-3"
                                  value={
                                    editingSlevaIndex === index
                                      ? String(item.sleva ?? '').trim()
                                      : (String(item.sleva ?? '').trim()
                                          ? `${String(item.sleva).trim()} %`
                                          : '')
                                  }
                                  onFocus={(e) => {
                                    setEditingSlevaIndex(index)
                                    setTimeout(() => e.target.select(), 0)
                                  }}
                                  onChange={(e) => {
                                    let raw = e.target.value.replace(/\./g, ',').replace(/[^\d,-]/g, '')
                                    raw = raw.replace(/(?!^)-/g, '')
                                    if (raw === '' || raw === '-') {
                                      handleItemChange(index, 'sleva', raw === '-' ? '-' : '')
                                      return
                                    }
                                    const n = Number.parseInt(raw, 10)
                                    if (!Number.isFinite(n)) {
                                      handleItemChange(index, 'sleva', '')
                                      return
                                    }
                                    const clamped = Math.max(-100, Math.min(100, n))
                                    handleItemChange(index, 'sleva', String(clamped))
                                  }}
                                  onBlur={() => {
                                    setEditingSlevaIndex(null)

                                    const raw = String(item.sleva ?? '').trim()
                                    if (raw === '' || raw === '-' || raw === '0' || raw === '-0') {
                                      handleItemChange(index, 'sleva', '')
                                      return
                                    }
                                    const n = Number.parseInt(raw, 10)
                                    if (!Number.isFinite(n)) {
                                      handleItemChange(index, 'sleva', '')
                                      return
                                    }
                                    const clamped = Math.max(-100, Math.min(100, n))
                                    handleItemChange(index, 'sleva', clamped === 0 ? '' : String(clamped))
                                  }}
                                  inputMode="text"
                                  title="Zadej slevu -100 až 100 (záporná hodnota = přirážka)"
                                  autoComplete="off"
                                />
                              </div>
                              {/* dph */}
                              <div className="p-1" style={{ width: '6%', minWidth: '50px' }}>
                                <Form.Control
                                  name="dph"
                                  className="p-3"
                                  disabled
                                  value={
                                    item.dph === '' || item.dph === 0 || item.dph === '0'
                                      ? ''
                                      : String(item.dph).endsWith('%')
                                        ? String(item.dph)
                                        : `${item.dph} %`
                                  }
                                  required
                                />
                              </div>
                              {/* celkem */}
                              <div className="p-1" style={{ width: '10%', minWidth: '125px' }}>
                                <Form.Control
                                  name="celkem"
                                  type="text"
                                  value={formatCZK(item.celkem)}
                                  className="p-3"
                                  disabled
                                  readOnly
                                />
                              </div>
                              {/* celkem s dph */}
                              <div className="p-1" style={{ width: '10%', minWidth: '125px' }}>
                                <Form.Control
                                  name="celkemsdph"
                                  type="text"
                                  value={formatCZK(item.celkemsdph)}
                                  className="p-3"
                                  disabled
                                  readOnly
                                />
                              </div>
                              <div
                                className="p-1 text-center"
                                style={{ width: '3%', minWidth: '50px' }}
                              >
                                <Button
                                  className="btn-table"
                                  variant="outline-light"
                                  title="Odstranit řádek"
                                  onClick={() => removeRow(index)}
                                  disabled={items.length === 1}
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      )
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>            
          </div>          
        </Col>  
        <Col sm={12}>
          <div className="d-flex flex-wrap">
            <Button variant="outline-primary" onClick={() => setShowGoodsModal(true)} className='me-1'>
              <i className="bi bi-plus-circle me-1"></i> Zboží
            </Button>
            <Button variant="outline-primary" className='me-1' onClick={() => setShowServiceModal(true)}>
              <i className="bi bi-plus-circle me-1"></i> Služba
            </Button>
            <Button variant="outline-primary" className='me-1' onClick={() => setShowMaterialModal(true)}>
              <i className="bi bi-plus-circle me-1"></i> Materiál
            </Button>
            <Button variant="outline-primary" className='me-1' onClick={addTextRow}>
              <i className="bi bi-plus-circle me-1"></i> Text
            </Button>
            {/* vyčištění položek */}
            <Button
              variant="outline-danger"
              className="ms-3"
              disabled={items.length === 1 && String(items[0]?.typradku ?? '1') === '1' && items[0].karta === '' && items[0].nazev === ''}
              onClick={() => setShowClearItemsModal(true)}
              title="Vyprázdnit všechny položky objednávky"
            >
              <i className="bi bi-trash me-1"></i> Vyčistit položky            
            </Button>
          </div>
        </Col>        
      </Row>
      {/* Poznámka a uložení */}
      <Row className='g-1 mb-3 justify-content-between'>
        <h5 className="mb-3 mt-3">Poznámka</h5>      
        <Col sm={6}>
          <Form.Group controlId="formPoznamka">
            <FloatingLabel label={<>poznámka</>}>
              <Form.Control
                as="textarea"
                name="poznamka"
                rows={4}
                value={header.poznamka}
                onChange={handleHeaderChange}
                style={{ height: 'auto', marginBottom: '14px' }}
                maxLength={500}
              />
            </FloatingLabel>
          </Form.Group>
        </Col>
        <Col sm={6} className='text-end mb-5'>
          <Button variant="secondary" className='p-3' onClick={() => navigate('/orders', { state: { type: 'backlink' } })} title='Zpět na přehled bez uložení'>
            <i className="bi bi-arrow-left me-1"></i> Zpět
          </Button>
          <Button type="submit" variant="primary" className='ms-1 p-3' title='Uložit změny v objednávce'>
            <i className="bi bi-save me-1"></i> Uložit
          </Button>
          {!isStateChangeLocked && (
            <Button type="button" variant="warning" className='ms-1 p-3' onClick={handleSubmitDilna} title='Uložit změny a předat objednávku do dílny'>
              <i className="bi bi-wrench me-1"></i> Do dílny
            </Button>
          )}
          {!isStateChangeLocked && (
            <Button type="button" variant="success" className='ms-1 p-3' onClick={handleSubmitUzavreno} title='Uložit změny a uzavřít objednávku'>
              <i className="bi bi-check2-circle me-1"></i> Uzavřeno
            </Button>
          )}
        </Col>
      </Row>
    </Form> 

    {/* Modál pro vložení nové položky ZBOŽÍ */}
    <Modal show={showGoodsModal} fullscreen="xl-down" onHide={() => closeGoodsModal()} keyboard={true} size="xl" centered scrollable>
      <Modal.Header closeButton className='border-0 p-4'>
        <Modal.Title className='text-primary lh-1'>Zboží<br/>
          <span className='text-muted fs-6'>výběr zboží</span>
        </Modal.Title>            
      </Modal.Header>
      <Modal.Body
        className="pt-0 d-flex flex-column"
        style={{
          minHeight: 'min(750px, calc(100dvh - 180px))',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))'
        }}
      >
        <Form.Group className='px-2 pt-2' controlId="hledat">
          <FloatingLabel label='hledat…'>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="tooltip-cena">
                  fulltextové vyhledávání v kartách podle čísla, názvu, kódu 1 nebo kódu 2, pole lze vzájemně kombinovat
                </Tooltip>
              }
            >
              <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
            </OverlayTrigger> 
            <Form.Control
              value={goodsSearch}
              onChange={(e) => {
                const val = e.target.value
                setGoodsSearch(val)
                searchGoods(val)
              }}
              placeholder="Hledat…"
              autoFocus
            />                    
          </FloatingLabel>
        </Form.Group>                   
        <div className="px-2 mt-3 flex-grow-1 overflow-auto position-relative" style={{ maxHeight: 350}}>
          {/* Header */}
          <div
            className="d-flex px-2 py-1 border-bottom bg-body text-muted"
            style={{ fontSize: 12, fontWeight: 600, position: "sticky", top: 0, zIndex: 2,  }}
          >
            <div style={{ width: 70, flex: "0 0 70px" }}>karta</div>
            <div style={{ flex: "1 1 auto", minWidth: 260 }}>název</div>
            <div style={{ width: 120, flex: "0 0 120px" }}>kód</div>
            <div style={{ width: 40, flex: "0 0 40px" }}>mj</div>
            <div style={{ width: 40, flex: "0 0 40px", textAlign: "right" }}>stav</div>
            <div style={{ width: 110, flex: "0 0 110px", textAlign: "right" }}>nákup</div>
            <div style={{ width: 110, flex: "0 0 110px", textAlign: "right" }}>prodej</div>
          </div>
          {/* LOADING – jen nad výsledky */}
          {goodsLoading && (
            <div
              className="position-absolute start-0 end-0 d-flex align-items-center justify-content-center"
              style={{
                top: 32,                
                bottom: 0,
                backgroundColor: isDark ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.6)',
                backdropFilter: 'blur(2px)',
                zIndex: 3,
              }}
            >
              <div className="text-center">
                <Spinner animation="border" variant={isDark ? 'light' : 'primary'} />
                <div className={`mt-2 small ${isDark ? 'text-light' : 'text-body-secondary'}`}>
                  Načítám zboží…
                </div>
              </div>
            </div>
          )}
          {/* Results */}          
          <div className="list-group list-group-flush" style={{ fontSize: 13 }}>
            {goodsResults.map((g) => {
              const stav = Number(g.stav ?? 0)
              const isSelected = selectedGoods?.karta === g.karta
              const isAdded = isGoodsAlreadyInOrder(g)            
              const stavVariant = stav > 0 ? "success" : stav === 0 ? "warning" : "danger"
              return (
                <button
                  type="button"
                  key={g.karta}
                  disabled={isAdded}
                  className="list-group-item list-group-item-action px-2 py-2"
                  onClick={() => {
                    if (isAdded) return
                    setSelectedGoods(g)
                    setGoodsQty(1)
                    setGoodsPrice(Number(g.cena ?? 0))
                    setGoodsDiscount(0)
                  }}
                  title={isAdded ? 'Položka už je v objednávce' : g.nazev}
                  style={{
                    cursor: isAdded ? 'not-allowed' : 'pointer',
                    opacity: 1,
                    backgroundColor: isAdded
                      ? 'rgba(13,110,253,0.08)'
                      : isSelected
                        ? 'rgba(108,117,125,0.10)' // jemně odlišíme jen aktivně vybranou
                        : undefined,
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div style={{ width: 70, flex: "0 0 70px", fontWeight: 700 }}>
                      {g.karta}
                    </div>
                    <div
                      style={{
                        flex: "1 1 auto",
                        minWidth: 260,
                        paddingRight: 12,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={g.nazev}
                    >
                      {g.nazev}
                      {isAdded && ( 
                        <span><i className="bi bi-check2-circle text-primary ms-2"></i></span> 
                      )}
                    </div>
                    <div style={{ width: 120, flex: "0 0 120px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={g.kod1 || ""}>
                      {g.kod1 || ""}
                    </div>
                    <div style={{ width: 40, flex: "0 0 40px" }}>{g.mj}</div>
                    <div style={{ width: 40, flex: "0 0 40px", textAlign: "right" }}>
                      <span className={`badge text-bg-${stavVariant}`}>{stav}</span>
                    </div>
                    <div style={{ width: 110, flex: "0 0 110px", textAlign: "right", fontWeight: 600 }}>
                      {Number(g.nakup ?? 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč
                    </div>
                    <div style={{ width: 110, flex: "0 0 110px", textAlign: "right", fontWeight: 600 }}>
                      {Number(g.cena ?? 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč
                    </div>                    
                  </div>
                </button>
              )
            })}
            {!hasSearch && !goodsLoading && (
              <div
                className="d-flex flex-column align-items-center justify-content-center text-muted mt-5 pt-5"
                style={{ height: '100%' }}
              >
                <i className="bi bi-search text-secondary opacity-25" style={{ fontSize: 32, marginBottom: 12 }}></i>
                <div className="fw-semibold mb-1">Vyhledej zboží</div>
                <div className="small text-center" style={{ maxWidth: 360 }}>
                  napiš alespoň <strong>3 znaky</strong><br />
                  (číslo, název, kód1 nebo kód2)
                </div>
              </div>
            )}
            {hasSearch && showNoGoods && !goodsLoading && (
              <div className="px-2 py-3 text-muted" style={{ fontSize: 13 }}>
                žádné položky neodpovídají hledání
              </div>
            )}
          </div>
        </div>
        {selectedGoods && (
          <div className="px-2">
            <hr className="hr my-3" />
            <div className="small text-muted mb-2">historie prodeje</div>
            <div className="table-responsive" style={{ maxHeight: 175, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
                <thead className="text-muted" style={{ fontSize: 12, position: 'sticky', top: 0, zIndex: 1, background: 'var(--bs-body-bg)' }}>
                  <tr>
                    <th className="text-muted">datum</th>
                    <th className="text-end text-muted">počet</th>
                    <th className="text-end text-muted">cena bez dph</th>
                    <th className="text-end text-muted">sleva</th>
                    <th className="text-end text-muted">cena po slevě bez dph</th>
                  </tr>
                </thead>
                <tbody>
                  {goodsHistoryLoading ? (
                    <tr><td colSpan={5} className="text-muted text-center">Načítám...</td></tr>
                  ) : goodsHistory.length > 0 ? (
                    goodsHistory.map((row, idx) => (
                      <tr key={`hist-${idx}`} className="text-muted">
                        <td>{formatDateCZ(row.datum)}</td>
                        <td className="text-end">{Number(row.pocet ?? 0).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="text-end">{Number(row.cena ?? 0).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč</td>
                        <td className="text-end">{Number(row.sleva ?? 0).toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %</td>
                        <td className="text-end">{Number(row.cenaPoSleve ?? 0).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={5} className="text-muted text-center">Nebyly nalezeny žádné údaje</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {selectedGoods && (
          <div className="px-2 pb-2">
            <hr className="hr"/>
            <Row className="g-2 align-items-end">
              <Col xs={12}>
                <div className="mb-2 small ">
                  vybraná položka: <span className="text-primary"><span className="me-2">{selectedGoods.karta}</span>{selectedGoods.nazev}</span>
                </div>
              </Col>
              <Col xs={3}>
                <Form.Group controlId="pocet">
                  <FloatingLabel label='počet mj'>
                    <Form.Control
                      type="number"
                      value={goodsQty}
                      onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                      onChange={(e) => setGoodsQty(e.target.value)}
                    />
                  </FloatingLabel>
                </Form.Group> 
              </Col>
              <Col xs={3}>
                <Form.Group controlId="cena">
                  <FloatingLabel label='cena'>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={goodsPrice}
                       onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                      onChange={(e) => setGoodsPrice(e.target.value)}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={3}>
                <Form.Group controlId="sleva">
                  <FloatingLabel label='sleva %'>
                    <Form.Control
                      type="text"
                      pattern="^-?\\d*$"
                      value={goodsDiscount}
                       onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                      onChange={(e) => setGoodsDiscount(e.target.value)}
                    />
                  </FloatingLabel>
                </Form.Group> 
              </Col>
              <Col className="d-flex gap-1">
              <Button
                className="btn p-3"
                onClick={addGoodsToItems}
                disabled={isSelectedGoodsAlreadyInOrder}
                title={isSelectedGoodsAlreadyInOrder ? 'Tato položka už je v objednávce' : 'Přidat položku'}
              >
                <i className={`bi ${isSelectedGoodsAlreadyInOrder ? 'bi-check2-circle' : 'bi-plus-circle'} me-2`}></i>
                {isSelectedGoodsAlreadyInOrder ? 'Přidáno' : 'Přidat'}
              </Button>
              <Button className="btn p-3" variant="secondary" onClick={closeGoodsModal}>
                <i className="bi bi-x-circle me-2"></i>Zavřít
              </Button>
              </Col>
            </Row>
          </div>
        )}
      </Modal.Body>
    </Modal>

    {/* Modál pro vložení nové položky SLUŽBA */}
    <Modal show={showServiceModal} fullscreen="xl-down" onHide={() => closeServiceModal()} keyboard={true} size="xl" centered scrollable>
      <Modal.Header closeButton className="border-0 p-4">
        <Modal.Title className="text-primary lh-1">
          Služby<br />
          <span className="text-muted fs-6">výběr servisních úkonů</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="pt-0 d-flex flex-column"
        style={{
          minHeight: 'min(750px, calc(100dvh - 180px))',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))'
        }}
      >
        {isServiceLoading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: isDark ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(2px)',
              zIndex: 10,
            }}
          >
            <div className="text-center">
              <Spinner animation="border" variant={isDark ? 'light' : 'primary'} />
              <div className={`mt-2 small ${isDark ? 'text-light' : 'text-body-secondary'}`}>
                načítám služby…
              </div>
            </div>
          </div>
        )}
        {/* FILTRY (vycentrované) */}
        <div className="pb-4">
          <div className="d-flex justify-content-center align-items-start flex-wrap gap-4">
            {/* BLOK: TYP VOZIDLA */}
            <div>
              <div className="service-section-title">Typ vozidla</div>
              <div className="d-flex justify-content-center flex-wrap gap-1">
              {SERVICE_TYPES.map(t => {
                const active = serviceTyp === t.code
                return (
                  <Button
                    key={t.code}
                    className={`btn-square p-1 ${active ? 'text-white' : ''}`}
                    style={{ minWidth: 70, borderRadius: 12 }}
                    variant={active ? 'primary' : 'outline-primary'}
                    onClick={() => handleTypChange(t.code)}
                  >
                    <i
                      className={`bi ${t.icon}`}
                      style={{ lineHeight: 1, color: active ? 'inherit' : 'inherit' }}
                    ></i>
                    <div className={`mt-1 ${active ? 'text-white' : ''}`} style={{ fontSize: 14 }}>
                      {t.label}
                    </div>
                  </Button>
                )
              })}
              </div>
            </div>
            {/* BLOK: SKUPINA */}
            {(SERVICE_GROUPS[serviceTyp]?.length ?? 0) > 0 && (
              <div>
                <div className="service-section-title">Skupina vozidel</div>
                <div className="d-flex justify-content-center flex-wrap gap-1">
                {SERVICE_GROUPS[serviceTyp].map(g => {
                  const active = serviceSku === g
                  return (
                    <Button
                      key={g}
                      className={`btn-square p-1 ${active ? 'text-white' : ''}`}
                      style={{ minWidth: 74, borderRadius: 12 }}
                      variant={active ? 'primary' : 'outline-primary'}
                      onClick={() => { setServiceSku(g); setActiveService(null) }}
                    >
                      {/* u active nesmí zůstat text-body-secondary, jinak to ztmavne */}
                      <div className={`fw-bold ${active ? 'text-white' : ''}`}>{g}</div>
                      <div className={`mt-1 ${active ? 'text-white' : ''}`} style={{ fontSize: 14 }}>{getGroupLabel(g)}</div>
                    </Button>
                  )
                })}
                </div>
              </div>
            )}
          </div>
        </div>            
        <div className="service-picker-layout px-2 pb-2">
          <div className="service-category-menu">
            <div className="service-section-title">Kategorie služby</div>
            {SERVICE_CATS.map((category) => {
              const active = serviceKat === category.code
              const available = availableServiceCategories.has(category.code)
              return (
                <Button
                  key={category.code}
                  type="button"
                  variant={active ? 'primary' : 'outline-primary'}
                  className="service-category-tile text-start"
                  disabled={!available}
                  onClick={() => { setServiceKat(category.code); setActiveService(null) }}
                  title={category.label}
                >
                  <i className={`bi ${category.icon || 'bi-bookmark-fill'} service-category-icon`}></i>
                  <span className="service-category-label">{category.label}</span>
                </Button>
              )
            })}
          </div>
          <div className="service-card-grid">
            <div className="service-section-title service-card-grid-title">Nabídka služeb</div>
            {categoryServices.map((svc) => {
              const selected = isServiceSelected(svc)
              const active = activeService?.karta === svc.karta
              return (
                <div
                  key={svc.karta}
                  role={selected ? undefined : 'button'}
                  tabIndex={selected ? -1 : 0}
                  onClick={() => { if (!selected) openServiceForm(svc) }}
                  onKeyDown={(event) => { if (!selected && (event.key === 'Enter' || event.key === ' ')) openServiceForm(svc) }}
                  className={`service-card-tile text-start ${selected ? 'is-selected' : active ? 'is-active' : ''}`}
                  title={selected ? 'Služba je v objednávce' : svc.nazev}
                >
                  <div className="service-card-main">
                    <span className="service-card-number">{svc.karta}</span>
                    <span className="service-card-name" title={svc.nazev}>{svc.nazev}</span>
                  </div>
                  <div className="service-card-side">
                    <span className="service-card-price">{Number(svc.cena ?? 0).toFixed(0)} Kč</span>
                    {selected ? (
                      <button type="button" className="service-card-delete" title="Odebrat z objednávky" aria-label={`Odebrat službu ${svc.nazev}`} onClick={(event) => { event.stopPropagation(); removeSelectedCard(svc.karta) }}>
                        <i className="bi bi-trash3"></i>
                      </button>
                    ) : <span />}
                  </div>
                </div>
              )
            })}
            {!categoryServices.length ? <div className="text-muted p-3">V této kategorii nejsou pro zvolený typ a skupinu žádné služby.</div> : null}
          </div>
        </div>
        {/* FORM PRO POČET/SLEVU – stejná linie jako u zboží */}
        <div className="service-selection-panel mx-2 mt-auto">
            <hr className="hr" />
            <Row className="g-2 align-items-end">
              <Col xs={12}>
                <div className="service-section-title">Vybraná položka</div>
                <div className={`mb-2 ${activeService ? 'text-primary' : 'text-muted'}`}>
                  {activeService ? <><span className="me-2">{activeService.karta}</span>{activeService.nazev}</> : 'Nejprve vyberte službu z nabídky.'}
                </div>
              </Col>
              <Col xs={12}>
                <OrderItemControls
                  activeItem={activeService}
                  quantity={serviceQty}
                  setQuantity={setServiceQty}
                  discount={serviceDiscount}
                  setDiscount={setServiceDiscount}
                  selected={isActiveServiceSelected}
                  onAdd={addActiveService}
                  onBack={closeServiceModal}
                  addTitle={isActiveServiceSelected ? 'Tato služba už je v objednávce' : 'Přidat službu'}
                />
              </Col>
            </Row>
        </div>
      </Modal.Body>
    </Modal>

    {/* Modál pro vložení nové položky MATERIÁL */}    
    <Modal show={showMaterialModal} fullscreen="xl-down" onHide={closeMaterialModal} keyboard={true} size="xl" centered scrollable>
      <Modal.Header closeButton className="border-0 p-4">
        <Modal.Title className="text-primary lh-1">
          Opravný materiál<br />
          <span className="text-muted fs-6">výběr spotřebního materiálu</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="pt-0 d-flex flex-column"
        style={{
          minHeight: 'min(750px, calc(100dvh - 180px))',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))'
        }}
      >
        {isMaterialLoading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: isDark ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(2px)',
              zIndex: 10,
            }}
          >
            <div className="text-center">
              <Spinner animation="border" variant={isDark ? 'light' : 'primary'} />
              <div className={`mt-2 small ${isDark ? 'text-light' : 'text-body-secondary'}`}>
                načítám opravný materiál…
              </div>
            </div>
          </div>
        )}
        <div className="service-picker-layout px-2 pb-2">
          <div className="service-category-menu">
            <div className="service-section-title">Kategorie materiálu</div>
            {MATERIAL_CATS.map(c => {
              const active = materialKat === c.code
              return (
                <Button
                  key={c.code}
                  type="button"
                  className="service-category-tile text-start"
                  variant={active ? 'primary' : 'outline-primary'}
                  onClick={() => { setMaterialKat(c.code); setActiveMaterial(null) }}
                >
                  <i className={`bi ${c.icon} service-category-icon`}></i>
                  <span className="service-category-label">{c.label}</span>
                </Button>
              )
            })}
          </div>
          <div className="service-card-grid">
            <div className="service-section-title service-card-grid-title">Nabídka materiálu</div>
            {filteredMaterial.map(mat => {
              const selected = isMaterialSelected(mat)
              const active = activeMaterial?.karta === mat.karta
              return (
                <div
                  key={mat.karta}
                  role={selected ? undefined : 'button'}
                  tabIndex={selected ? -1 : 0}
                  onClick={() => { if (!selected) openMaterialForm(mat) }}
                  onKeyDown={(event) => { if (!selected && (event.key === 'Enter' || event.key === ' ')) openMaterialForm(mat) }}
                  className={`service-card-tile text-start ${selected ? 'is-selected' : active ? 'is-active' : ''}`}
                  title={selected ? 'Materiál je v objednávce' : mat.nazev}
                >
                  <div className="service-card-main">
                    <span className="service-card-number">{mat.karta}</span>
                    <span className="service-card-name" title={mat.nazev}>{mat.nazev}</span>
                  </div>
                  <div className="service-card-side">
                    <span className="service-card-price">{Number(mat.cena ?? 0).toFixed(0)} Kč</span>
                    {selected ? <button type="button" className="service-card-delete" title="Odebrat z objednávky" aria-label={`Odebrat materiál ${mat.nazev}`} onClick={(event) => { event.stopPropagation(); removeSelectedCard(mat.karta) }}><i className="bi bi-trash3"></i></button> : <span />}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        {/* FORM PRO POČET/SLEVU  – stejný jako u zboží/služeb */}
        <div className="service-selection-panel mx-2 mt-auto">
            <hr className="hr" />
            <Row className="g-2 align-items-end">
              <Col xs={12}>
                <div className="service-section-title">Vybraná položka</div>
                <div className={`mb-2 ${activeMaterial ? 'text-primary' : 'text-muted'}`}>{activeMaterial ? <><span className="me-2">{activeMaterial.karta}</span>{activeMaterial.nazev}</> : 'Nejprve vyberte materiál z nabídky.'}</div>
              </Col>
              <Col xs={12}>
                <OrderItemControls
                  activeItem={activeMaterial}
                  quantity={materialQty}
                  setQuantity={setMaterialQty}
                  discount={materialDiscount}
                  setDiscount={setMaterialDiscount}
                  selected={isActiveMaterialSelected}
                  onAdd={addActiveMaterial}
                  onBack={closeMaterialModal}
                  addTitle={isActiveMaterialSelected ? 'Tato položka už je v objednávce' : 'Přidat položku'}
                />
              </Col>
            </Row>
        </div>
      </Modal.Body>
    </Modal>

    {/* Modál pro vyčištění všech položek */}
    <Modal show={showClearItemsModal} fullscreen="xl-down" onHide={() => setShowClearItemsModal(false)} keyboard={true} size="lg" aria-labelledby="contained-modal-title-vcenter" centered scrollable>
      <Modal.Header closeButton className='border-0 p-4'>
        <Modal.Title className='text-primary lh-1'>Upozornění<br/>
          <span className='text-muted fs-6'>vymazání položek objednávky</span>
        </Modal.Title>            
      </Modal.Header>
      <Modal.Body className='px-4 pt-0'> 
        <Row className='g-2'> 
          <Col xs={12}>              
            <Alert variant="danger" className="d-flex align-items-center mb-4">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <span>Opravdu chcete odstranit všechny položky objednávky? Tuto akci nelze vrátit zpět.</span>
            </Alert>
            <Button className='me-1'
              variant="danger"
              onClick={() => {
                setItems([])
                addRow()
                setShowClearItemsModal(false)
                const newNotification = {
                  id: Date.now(),
                  type: 'success',
                  message: `Položky objednávky byly vymazány.`,
                  time: new Date().toLocaleTimeString(),
                }
                setNotifications(prev => [...prev, newNotification])
              }}
            >
              Odstranit
            </Button>
            <Button className='me-1'
              variant="secondary"
              onClick={() => setShowClearItemsModal(false)}
            >
              Zrušit
            </Button>              
          </Col>
        </Row>                  
      </Modal.Body>
    </Modal> 

    {/* Modál pro novou hlavní adresu */}
    <Modal show={showAddAddressModal} fullscreen="xl-down" onHide={() => closeModal()} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered scrollable>
      <Modal.Header closeButton className='border-0 p-4'>
        <Modal.Title className='text-primary lh-1'>Adresa<br/>
          <span className='text-muted fs-6'>přidat nový záznam</span>
        </Modal.Title>            
      </Modal.Header>
      <Form id='formAddAddressModal' noValidate validated={validatedAddress} onSubmit={handleAddAddress} className='overflow-auto' >
        <Modal.Body className='px-4'> 
          <Row className='g-2'> 
            <Col xs={12}>
              {isError && (
                <Alert variant="danger" className="d-flex align-items-center mb-0">
                  <i className="bi bi-exclamation-triangle pe-2"></i>
                  <span>{errorMsg}</span>
                </Alert>
              )}
            </Col>   
            <Col xs={6}>
              <Form.Group controlId="formIco">
                <FloatingLabel label='ič'>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-cena">
                        automatické ověření v registru ekonomických subjektů podle zadaného IČ, u nefiremních zákazníků toto pole nevyplňujte
                      </Tooltip>
                    }
                  >
                    <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                  </OverlayTrigger> 
                  <Form.Control
                    type="text"
                    name='ico'
                    pattern="^\d+$"
                    maxLength={12}
                    value={icoInput}
                    onChange={handleIcoChange}
                  />                      
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group controlId="formDic">
                <FloatingLabel label="dič">
                <Form.Control
                  type="text"
                  name="dic"
                  defaultValue={newAddress.dic}
                  readOnly
                  disabled
                />
                </FloatingLabel>
              </Form.Group>
            </Col>   
            {dphStatus && (
              <Col xs={12}>
                {dphStatus === 'ANO' && (
                  <Alert variant="danger" className="d-flex align-items-center mb-0 py-1 px-2">
                    <i className="bi bi-exclamation-triangle pe-2"></i>
                    <span>Nespolehlivý plátce DPH</span>
                  </Alert>
                )}

                {dphStatus === 'NE' && (
                  <Alert variant="success" className="d-flex align-items-center mb-0 py-1 px-2">
                    <i className="bi bi-check-circle pe-2"></i>
                    <span>Spolehlivý plátce DPH</span>
                  </Alert>
                )}

                {dphStatus === 'NENALEZEN' && (
                  <Alert variant="warning" className="d-flex align-items-center mb-0 py-1 px-2">
                    <i className="bi bi-question-circle pe-2"></i>
                    <span>Spolehlivost plátce nelze ověřit</span>
                  </Alert>
                )}

                {dphStatus?.error && (
                  <Alert variant="warning" className="d-flex align-items-center mb-0 py-1 px-2">
                    <i className="bi bi-exclamation-circle pe-2"></i>
                    <span>{dphStatus.error}</span>
                  </Alert>
                )}
              </Col>
            )}
            <Col xs={12 }>
              <Form.Group controlId="formFirma">
                <FloatingLabel label={<>jméno nebo název firmy <span className="text-danger">*</span></>}>                    
                  <Form.Control
                    type="text"
                    name='firma' 
                    defaultValue={newAddress.firma}
                    maxLength={50}
                    required
                  />
                </FloatingLabel>
              </Form.Group> 
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formUllice">
                <FloatingLabel label={<>ulice <span className="text-danger">*</span></>}>
                <Form.Control
                  type="text"
                  name="ulice"
                  defaultValue={newAddress.ulice}
                  maxLength={30}
                  required
                />
                </FloatingLabel>
              </Form.Group> 
            </Col>      
            <Col sm={4}>
              <Form.Group controlId="formPsc">                    
                <FloatingLabel label={<>psč <span className="text-danger">*</span></>}>
                  <FormControl
                    type="tel"
                    name='psc'  
                    defaultValue={newAddress.psc}
                    pattern="^\d{5}$"
                    title="Zadejte 5 číslic bez mezer"
                    required
                  />                       
                </FloatingLabel>                                      
              </Form.Group>
            </Col>
            <Col sm={8}>
              <Form.Group controlId="formMesto">
                <FloatingLabel label={<>město <span className="text-danger">*</span></>}>
                <Form.Control
                  type="text"
                  name="mesto"
                  defaultValue={newAddress.mesto}
                  maxLength={30}
                  required
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formTelefon">                    
                <InputGroup>
                  <FormSelect name="predvolba" style={{ maxWidth: '150px' }}>
                    <option value="+420">🇨🇿 +420</option>
                    <option value="+421">🇸🇰 +421</option>
                    <option value="+48">🇵🇱 +48</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+43">🇦🇹 +43</option>
                  </FormSelect>
                  <FloatingLabel label='telefon'>
                    <FormControl
                      type="tel"
                      name='telefon'
                      pattern="^\d{9}$"
                      title="Zadejte 9 číslic bez mezer"                        
                    />                             
                  </FloatingLabel>                      
                </InputGroup>                                        
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formEmail">
                <FloatingLabel label={<>email</>}>
                  <Form.Control
                    type="email"
                    name='email'
                    maxLength={64}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>                                                                                        
            <Col xs={12}>
              <Form.Group controlId="formPoznamka">
                  <FloatingLabel label={<>poznámka</>}>
                    <Form.Control
                      as="textarea"
                      name="poznamka"
                      style={{ height: '100px' }}
                      maxLength={500}
                    />
                  </FloatingLabel>
                </Form.Group>
            </Col>              
          </Row>                  
        </Modal.Body>
          <Modal.Footer className='border-0 d-flex justify-content-between px-4 pb-3'>
            <div>
            <Button variant="primary me-1" type="submit">Uložit</Button>
            <Button variant="secondary" onClick={() => closeModal()}>Zavřít</Button>            
            </div>
              <p className='text-muted small mt-2 mb-0'><span className="text-danger me-1">*</span>označení povinných polí</p>
          </Modal.Footer>
      </Form>
    </Modal>

    {/* Modál pro změnu hlavní adresy */}
    <Modal show={showEditAddressModal} fullscreen="xl-down" onHide={() => closeModal()} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered scrollable>
      <Modal.Header closeButton className='border-0 p-4'>
        <Modal.Title className='text-primary lh-1'>Adresa<br/>
          <span className='text-muted fs-6'>upravit existujcí záznam</span>
        </Modal.Title>            
      </Modal.Header>
      <Form id='formEditAddressModal' noValidate validated={validatedAddress} onSubmit={handleEditAddress} className='overflow-auto' >
        <Modal.Body className='px-4'> 
          <Row className='g-2'> 
            <Col xs={12}>
              {isError && (
                <Alert variant="danger" className="d-flex align-items-center mb-0 ">
                  <i className="bi bi-exclamation-triangle pe-2"></i>
                  <span>{errorMsg}</span>
                </Alert>
              )}
            </Col>   
            <Col xs={6}>
              <Form.Group controlId="formIco">
                <FloatingLabel label='ič'>                     
                  <Form.Control
                    type="text"
                    name='ico'
                    pattern="^\d+$"
                    maxLength={12}
                    value={header.ico}
                    disabled
                    readOnly
                  />                      
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group controlId="formDic">
                <FloatingLabel label="dič">
                <Form.Control
                  type="text"
                  name="dic"
                  defaultValue={header.dic}
                  readOnly
                  disabled
                />
                </FloatingLabel>
              </Form.Group>
            </Col>                 
            <Col xs={12 }>
              <Form.Group controlId="formFirma">
                <FloatingLabel label={<>jméno nebo název firmy <span className="text-danger">*</span></>}>                    
                  <Form.Control
                    type="text"
                    name='firma' 
                    defaultValue={header.firma}
                    maxLength={50}
                    required
                  />
                </FloatingLabel>
              </Form.Group> 
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formUllice">
                <FloatingLabel label={<>ulice <span className="text-danger">*</span></>}>
                <Form.Control
                  type="text"
                  name="ulice"
                  defaultValue={header.ulice}
                  maxLength={30}
                  required
                />
                </FloatingLabel>
              </Form.Group> 
            </Col>      
            <Col sm={4}>
              <Form.Group controlId="formPsc">                    
                <FloatingLabel label={<>psč <span className="text-danger">*</span></>}>
                  <FormControl
                    type="tel"
                    name='psc'  
                    defaultValue={header.psc}
                    pattern="^\d{5}$"
                    title="Zadejte 5 číslic bez mezer"
                    required
                  />                       
                </FloatingLabel>                                      
              </Form.Group>
            </Col>
            <Col sm={8}>
              <Form.Group controlId="formMesto">
                <FloatingLabel label={<>město <span className="text-danger">*</span></>}>
                <Form.Control
                  type="text"
                  name="mesto"
                  defaultValue={header.mesto}
                  maxLength={30}
                  required
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formTelefon">
                <FloatingLabel label='telefon'>
                <Form.Control
                  type="text"
                  name="telefon"
                  defaultValue={header.telefon}
                  maxLength={30}
                />
                </FloatingLabel>
              </Form.Group>                
            </Col>              
            <Col xs={12}>
              <Form.Group controlId="formEmail">
                <FloatingLabel label={<>email</>}>
                  <Form.Control
                    type="email"
                    name='email'
                    maxLength={64}
                    defaultValue={header.email}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>                                                                                        
            <Col xs={12}>
              <Form.Group controlId="formPoznamka">
                  <FloatingLabel label={<>poznámka</>}>
                    <Form.Control
                      as="textarea"
                      name="poznamka"
                      style={{ height: '100px' }}
                      maxLength={500}
                      defaultValue={header.adrpoznamka}
                    />
                  </FloatingLabel>
                </Form.Group>
            </Col>              
          </Row>                   
        </Modal.Body>
          <Modal.Footer className='border-0 d-flex justify-content-between px-4 pb-3'>
            <div>
            <Button variant="primary me-1" type="submit">Uložit</Button>
            <Button variant="secondary" onClick={() => closeModal()}>Zavřít</Button>            
            </div>
              <p className='text-muted small mt-2 mb-0'><span className="text-danger me-1">*</span>označení povinných polí</p>
          </Modal.Footer>
      </Form>
    </Modal>

    {/* Modál pro novou adresu konečného příjemce */}
    <Modal show={showAddAddressModalKP} fullscreen="xl-down" onHide={() => closeModal()} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered scrollable>
      <Modal.Header closeButton className='border-0 p-4'>
        <Modal.Title className='text-primary lh-1'>Konečný příjemce<br/>
          <span className='text-muted fs-6'>přidat nový záznam</span>
        </Modal.Title>            
      </Modal.Header>
      <Form id='formAddAddressModalKP' noValidate validated={validatedAddress} onSubmit={handleAddAddressKP} className='overflow-auto' >
        <Modal.Body className='px-4'> 
          <Row className='g-2'> 
            <Col xs={12}>
              {isError && (
                <Alert variant="danger" className="d-flex align-items-center mb-0 ">
                  <i className="bi bi-exclamation-triangle pe-2"></i>
                  <span>{errorMsg}</span>
                </Alert>
              )}
            </Col>   
            <Col xs={6}>
              <Form.Group controlId="formKPIco">
                <FloatingLabel label={<>ič <span className="text-danger">*</span></>}>                    
                  <Form.Control
                    type="text"
                    name='ico'
                    pattern="^[0-9/]{1,12}$"
                    maxLength={12}
                    required
                    onBlur={(e) => { e.target.value = e.target.value.replace(/\s+/g, '') }}
                  />                      
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group controlId="formKPDic">
                <FloatingLabel label={<>dič <span className="text-danger">*</span></>}>
                <Form.Control
                  type="text"
                  name="dic"
                  pattern="^[A-Z0-9]{1,17}$"
                  maxLength={17}
                  required
                  onBlur={(e) => { e.target.value = e.target.value.replace(/\s+/g, '').toUpperCase() }}
                />
                </FloatingLabel>
              </Form.Group>
            </Col>                 
            <Col xs={12 }>
              <Form.Group controlId="formKPFirma">
                <FloatingLabel label={<>jméno nebo název firmy <span className="text-danger">*</span></>}>                    
                  <Form.Control
                    type="text"
                    name='firma' 
                    maxLength={50}
                    required
                  />
                </FloatingLabel>
              </Form.Group> 
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formKPUllice">
                <FloatingLabel label={<>ulice <span className="text-danger">*</span></>}>
                <Form.Control
                  type="text"
                  name="ulice"
                  maxLength={30}
                  required
                />
                </FloatingLabel>
              </Form.Group> 
            </Col>      
            <Col sm={4}>
              <Form.Group controlId="formKPPsc">                    
                <FloatingLabel label={<>psč <span className="text-danger">*</span></>}>
                  <FormControl
                    type="tel"
                    name='psc'  
                    pattern="^\d{5}$"
                    title="Zadejte 5 číslic bez mezer"
                    required
                    onBlur={(e) => { e.target.value = e.target.value.replace(/\s+/g, '') }}
                  />                       
                </FloatingLabel>                                      
              </Form.Group>
            </Col>
            <Col sm={8}>
              <Form.Group controlId="formKPMesto">
                <FloatingLabel label={<>město <span className="text-danger">*</span></>}>
                <Form.Control
                  type="text"
                  name="mesto"
                  maxLength={30}
                  required
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formKPTelefon">                    
                <InputGroup>
                  <FormSelect name="predvolba" style={{ maxWidth: '150px' }}>
                    <option value="+420">🇨🇿 +420</option>
                    <option value="+421">🇸🇰 +421</option>
                    <option value="+48">🇵🇱 +48</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+43">🇦🇹 +43</option>
                  </FormSelect>
                  <FloatingLabel label='telefon'>
                    <FormControl
                      type="tel"
                      name='telefon'
                      pattern="^\d{9}$"
                      title="Zadejte 9 číslic bez mezer" 
                      onBlur={(e) => { e.target.value = e.target.value.replace(/\s+/g, '') }}                       
                    />                             
                  </FloatingLabel>                      
                </InputGroup>                                        
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formKPEmail">
                <FloatingLabel label={<>email</>}>
                  <Form.Control
                    type="email"
                    name='email'
                    maxLength={64}
                    onBlur={(e) => { e.target.value = e.target.value.replace(/\s+/g, '') }}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>                                                                                        
            <Col xs={12}>
              <Form.Group controlId="formKPPoznamka">
                  <FloatingLabel label={<>poznámka</>}>
                    <Form.Control
                      as="textarea"
                      name="poznamka"
                      style={{ height: '100px' }}
                      maxLength={500}
                    />
                  </FloatingLabel>
                </Form.Group>
            </Col>              
          </Row>                  
        </Modal.Body>
          <Modal.Footer className='border-0 d-flex justify-content-between px-4 pb-3'>
            <div>
            <Button variant="primary me-1" type="submit">Uložit</Button>
            <Button variant="secondary" onClick={() => closeModal()}>Zavřít</Button>            
            </div>
              <p className='text-muted small mt-2 mb-0'><span className="text-danger me-1">*</span>označení povinných polí</p>
          </Modal.Footer>
      </Form>
    </Modal>

    {/* Modál pro změnu adresy konečného příjemce */}
    <Modal show={showEditAddressModalKP} fullscreen="xl-down" onHide={() => closeModal()} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered scrollable>
      <Modal.Header closeButton className='border-0 p-4'>
        <Modal.Title className='text-primary lh-1'>Konečný příjemce<br/>
          <span className='text-muted fs-6'>upravit existujcí záznam</span>
        </Modal.Title>            
      </Modal.Header>
      <Form id='formEditAddressModalKP' noValidate validated={validatedAddress} onSubmit={handleEditAddressKP} className='overflow-auto' >
        <Modal.Body className='px-4'> 
          <Row className='g-2'> 
            <Col xs={12}>
              {isError && (
                <Alert variant="danger" className="d-flex align-items-center mb-0 ">
                  <i className="bi bi-exclamation-triangle pe-2"></i>
                  <span>{errorMsg}</span>
                </Alert>
              )}
            </Col>   
            <Col xs={6}>
              <Form.Group controlId="formKPIco">
                <FloatingLabel label='ič'>                     
                  <Form.Control
                    type="text"
                    name='icoprij'
                    pattern="^\d+$"
                    maxLength={12}
                    value={header.icoprij}
                    disabled
                    readOnly
                  />                      
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group controlId="formKPDic">
                <FloatingLabel label="dič">
                <Form.Control
                  type="text"
                  name="kpdic"
                  defaultValue={header.kpdic}
                  readOnly
                  disabled
                />
                </FloatingLabel>
              </Form.Group>
            </Col>                 
            <Col xs={12 }>
              <Form.Group controlId="formKPFirma">
                <FloatingLabel label={<>jméno nebo název firmy <span className="text-danger">*</span></>}>                    
                  <Form.Control
                    type="text"
                    name='kpfirma' 
                    defaultValue={header.kpfirma}
                    maxLength={50}
                    required
                  />
                </FloatingLabel>
              </Form.Group> 
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formKPUlice">
                <FloatingLabel label={<>ulice <span className="text-danger">*</span></>}>
                <Form.Control
                  type="text"
                  name="kpulice"
                  defaultValue={header.kpulice}
                  maxLength={30}
                  required
                />
                </FloatingLabel>
              </Form.Group> 
            </Col>      
            <Col sm={4}>
              <Form.Group controlId="formKPPsc">                    
                <FloatingLabel label={<>psč <span className="text-danger">*</span></>}>
                  <FormControl
                    type="tel"
                    name='kppsc'  
                    defaultValue={header.kppsc}
                    pattern="^\d{5}$"
                    title="Zadejte 5 číslic bez mezer"
                    required
                  />                       
                </FloatingLabel>                                      
              </Form.Group>
            </Col>
            <Col sm={8}>
              <Form.Group controlId="formKPMesto">
                <FloatingLabel label={<>město <span className="text-danger">*</span></>}>
                <Form.Control
                  type="text"
                  name="kpmesto"
                  defaultValue={header.kpmesto}
                  maxLength={30}
                  required
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formKPTelefon">
                <FloatingLabel label='telefon'>
                <Form.Control
                  type="text"
                  name="kptelefon"
                  defaultValue={header.kptelefon}
                  maxLength={30}
                />
                </FloatingLabel>
              </Form.Group>                
            </Col>              
            <Col xs={12}>
              <Form.Group controlId="formKPEmail">
                <FloatingLabel label={<>email</>}>
                  <Form.Control
                    type="email"
                    name='kpemail'
                    maxLength={64}
                    defaultValue={header.kpemail}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>                                                                                        
            <Col xs={12}>
              <Form.Group controlId="formKPPoznamka">
                  <FloatingLabel label={<>poznámka</>}>
                    <Form.Control
                      as="textarea"
                      name="kppoznamka"
                      style={{ height: '100px' }}
                      maxLength={500}
                      defaultValue={header.kpadrpoznamka}
                    />
                  </FloatingLabel>
                </Form.Group>
            </Col>              
          </Row>                  
        </Modal.Body>
          <Modal.Footer className='border-0 d-flex justify-content-between px-4 pb-3'>
            <div>
            <Button variant="primary me-1" type="submit">Uložit</Button>
            <Button variant="secondary" onClick={() => closeModal()}>Zavřít</Button>            
            </div>
              <p className='text-muted small mt-2 mb-0'><span className="text-danger me-1">*</span>označení povinných polí</p>
          </Modal.Footer>
      </Form>
    </Modal>

    {/* Modál pro bonus kartu založení i změna */}
    <Modal
      show={showBonusModal}
      fullscreen="xl-down"
      onHide={closeModal}
      keyboard={true}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      scrollable
    >
      <Modal.Header closeButton className='border-0 p-4'>
        <Modal.Title className='text-primary lh-1'>
          Bonus karta<br/>
          <span className='text-muted fs-6'>
            {mode === 'add' ? 'přidat nový záznam' : 'upravit existující záznam'}
          </span>
        </Modal.Title>            
      </Modal.Header>
      <Form
        id='formBonusModal'
        noValidate
        validated={validatedBonus}
        onSubmit={mode === 'add' ? handleAddBonus : handleEditBonus}
        className='overflow-auto'
      >
        <Modal.Body className='px-4'>
          <Row className='g-2'>
            <Col xs={12}>
              {isError && (
                <Alert variant="danger" className="d-flex align-items-center mb-0">
                  <i className="bi bi-exclamation-triangle pe-2"></i>
                  <span>{errorMsg}</span>
                </Alert>
              )}
            </Col>
            <Col xs={6}>
              <Form.Group controlId="formCislo">
                <FloatingLabel label={<>číslo karty <span className="text-danger">*</span></>}>
                  <Form.Control
                    type="text"
                    name='cislo'
                    pattern="^900\d{4}$"
                    title="Zadejte 7 číslic bez mezer, začínající 900xxxx"
                    maxLength={7}
                    required
                    defaultValue={mode === 'edit' ? header.bonus : ''}
                    readOnly={mode === 'edit'} // nelze měnit číslo karty při editaci
                    disabled={mode === 'edit'}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formJmeno">
                <FloatingLabel label={<>jméno a příjmení <span className="text-danger">*</span></>}>
                  <Form.Control
                    type="text"
                    name="jmeno"
                    maxLength={50}
                    required
                    defaultValue={mode === 'edit' ? header.bonusjmeno : ''}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formTelefon">
                <InputGroup>
                  {(() => {
                    const prefixes = ["+420", "+421", "+48", "+49", "+43"]
                    let predvolba = "+420"
                    let telefon = ""
                    if (mode === "edit" && header.bonustelefon) {
                      const found = prefixes.find((p) => header.bonustelefon.startsWith(p))
                      if (found) {
                        predvolba = found
                        telefon = header.bonustelefon.slice(found.length)
                      } else {
                        // fallback: celé číslo uložené bez předvolby
                        predvolba = "+420"
                        telefon = header.bonustelefon
                      }
                    }
                    return (
                      <>
                        <FormSelect
                          name="predvolba"
                          style={{ maxWidth: "150px" }}
                          defaultValue={predvolba}
                        >
                          <option value="+420">🇨🇿 +420</option>
                          <option value="+421">🇸🇰 +421</option>
                          <option value="+48">🇵🇱 +48</option>
                          <option value="+49">🇩🇪 +49</option>
                          <option value="+43">🇦🇹 +43</option>
                        </FormSelect>
                        <FloatingLabel label={<>telefon <span className="text-danger">*</span></>}>
                          <FormControl
                            type="tel"
                            name="telefon"
                            pattern="^\d{9}$"
                            title="Zadejte 9 číslic bez mezer"
                            required
                            defaultValue={telefon}
                          />
                        </FloatingLabel>
                      </>
                    )
                  })()}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formEmail">
                <FloatingLabel label='email'>
                  <Form.Control
                    type="email"
                    name='email'
                    maxLength={50}
                    defaultValue={mode === 'edit' ? header.bonusemail : ''}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formAdresa">
                <FloatingLabel label='adresa'>
                  <FormControl
                    type="text"
                    name='adresa'
                    maxLength={50}
                    defaultValue={mode === 'edit' ? header.bonusadresa : ''}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className='border-0 d-flex justify-content-between px-4 pb-3'>
          <div>
            <Button variant="primary me-1" type="submit">
              {mode === 'add' ? 'Uložit' : 'Upravit'}
            </Button>
            <Button variant="secondary" onClick={closeModal}>Zavřít</Button>
          </div>
          <p className='text-muted small mt-2 mb-0'><span className="text-danger me-1">*</span>označení povinných polí</p>
        </Modal.Footer>
      </Form>
    </Modal>

    <Notification notifications={notifications} setNotifications={setNotifications} />
    </>
  )
}
  
export default OrderUpdate
